import DOMPurify from 'dompurify';
import IMask from 'imask';
import SignaturePad from 'signature_pad';

var TextPlaceholderType;
(function (TextPlaceholderType) {
    TextPlaceholderType[TextPlaceholderType["ThankfulPage"] = 0] = "ThankfulPage";
})(TextPlaceholderType || (TextPlaceholderType = {}));

class SelectorUtil {
    static { this.sectionAttr = 'data-section'; }
    static { this.submitForAttr = 'data-submit-for'; }
    static { this.rowAttr = 'data-mesh-row'; }
    static { this.columnAttr = 'data-mesh-col'; }
    static { this.meshContainerAttr = 'data-mesh-container'; }
    static { this.meshAttr = 'data-mesh'; }
    static { this.dataSectionAreaAttr = 'data-section-area'; }
    static { this.elementAttr = 'data-element'; }
    static { this.widgetAttr = 'data-widget'; }
    static { this.fieldAttr = 'data-field'; }
    static { this.elementGroupAttr = 'data-group'; }
    static { this.variantAttr = 'data-variant'; }
    static { this.hiddenVarsBlockId = 'hidden-fields-container'; }
    static { this.hiddenVarsBlockAttr = 'data-hidden-vars'; }
    static { this.pageAttr = 'data-page'; }
    static { this.section = {
        base: `[${this.sectionAttr}]`,
        form: `[${this.sectionAttr}="form"]`,
        header: `[${this.sectionAttr}="header"]`,
        footer: `[${this.sectionAttr}="footer"]`,
        common: `[${this.sectionAttr}="common"]`,
        submit: `[${this.sectionAttr}="submit"]`
    }; }
    static { this.row = {
        base: `[${this.rowAttr}]`
    }; }
    static { this.column = {
        base: `[${this.columnAttr}]`
    }; }
    static { this.mesh = {
        base: `[${this.meshAttr}]`,
        meshContainer: `[${this.meshAttr}] [${this.meshContainerAttr}]`
    }; }
    static { this.dataSectionArea = {
        base: `[${this.dataSectionAreaAttr}]`,
        content: `[${this.dataSectionAreaAttr}] .content`
    }; }
    static { this.hiddenVarsBlock = {
        base: `[${this.hiddenVarsBlockAttr}]`
    }; }
    static { this.page = {
        base: `[${this.pageAttr}]`
    }; }
    static { this.builderElement = {
        base: `[${this.elementAttr}]`,
        field: `[${this.elementAttr}="field"]`,
        widget: `[${this.elementAttr}="widget"]`,
        hidden: `[${this.elementAttr}="hidden"]`
    }; }
    static { this.widget = {
        base: `[${this.widgetAttr}]`,
        submit: `[${this.widgetAttr}="submit"]`,
        divider: `[${this.widgetAttr}="divider"]`,
        text: `[${this.widgetAttr}="text"]`,
        header: `[${this.widgetAttr}="header"]`,
        image: `[${this.widgetAttr}="image"]`,
        spacer: `[${this.widgetAttr}="spacer"]`,
        signin: `[${this.widgetAttr}="signin"]`,
        signup: `[${this.widgetAttr}="signup"]`,
        forgotpassword: `[${this.widgetAttr}="forgotpassword"]`,
        navbutton: `[${this.widgetAttr}="navbutton"]`
    }; }
    static { this.field = {
        base: `[${this.fieldAttr}]`,
        text: `[${this.fieldAttr}="text"]`,
        email: `[${this.fieldAttr}="email"]`,
        firstname: `[${this.fieldAttr}="firstname"]`,
        lastname: `[${this.fieldAttr}="lastname"]`,
        companyname: `[${this.fieldAttr}="companyname"]`,
        birthday: `[${this.fieldAttr}="birthday"]`,
        signature: `[${this.fieldAttr}="signature"]`,
        subscribe: `[${this.fieldAttr}="subscribe"]`,
        file: `[${this.fieldAttr}="file"]`,
        textarea: `[${this.fieldAttr}="textarea"]`,
        number: `[${this.fieldAttr}="number"]`,
        tel: `[${this.fieldAttr}="tel"]`,
        radio: `[${this.fieldAttr}="radio"]`,
        date: `[${this.fieldAttr}="date"]`,
        url: `[${this.fieldAttr}="url"]`,
        checkbox: `[${this.fieldAttr}="checkbox"]`,
        dropdown: `[${this.fieldAttr}="dropdown"]`,
        hidden: `[${this.fieldAttr}="hidden"]`,
        password: `[${this.fieldAttr}="password"]`,
        captcha: `[${this.fieldAttr}="captcha"]`,
        gdpr: `[${this.fieldAttr}="gdpr"]`,
    }; }
    static hiddenVarsBlockIdFor(pageId) {
        return `${this.hiddenVarsBlockId}-${pageId}`;
    }
}

class DomUtils {
    static generateElementId(length = 5) {
        const alphabet = '0123456789abcdefghiklmnopqrstuvwxyz'.split('');
        let str = '';
        for (let i = 0; i < length; i++) {
            str += alphabet[Math.floor(Math.random() * alphabet.length)];
        }
        return `tfb-${str}`;
    }
    /**
     * Generate a unique ID for the current document.
     * @param blackList
     * IDs that cannot be generated.
     * @param length
     * Initial length of the generated ID.
     * @param sameLengthAttempts
     * Maximum number of attempts to generate a new ID, after which, if a unique ID has not yet been generated,
     * the length of the generated ID will be increased by 1 character.
     */
    static generateUniqueElementId(blackList, length = 5, sameLengthAttempts = 100) {
        let id = DomUtils.generateElementId(length);
        let generationAttemptCounter = 0;
        const sameLengthMaxNumberGenerationAttempts = blackList.length + sameLengthAttempts;
        while (document.getElementById(id) || blackList.includes(id)) {
            id = DomUtils.generateElementId(length);
            if (generationAttemptCounter > sameLengthMaxNumberGenerationAttempts) {
                length += 1;
                generationAttemptCounter = 0;
                continue;
            }
            generationAttemptCounter++;
        }
        return id;
    }
    static deleteAllChildElements(domNode) {
        while (domNode.firstChild) {
            domNode.removeChild(domNode.firstChild);
        }
    }
}

class BaseBuilderEntity {
    constructor(id, htmlElement) {
        this.id = id;
        this.htmlElement = htmlElement;
    }
    // Do not use before searching by builder entities if it is required before deleting
    remove() {
        this.htmlElement.remove();
    }
    notify(eventType, additionalInfo) {
        const event = this.createEvent(eventType, additionalInfo);
        document.dispatchEvent(event);
    }
    createEvent(type, additionalInfo) {
        const data = {
            id: this.id,
            additionalInfo: additionalInfo
        };
        return new CustomEvent(type, {
            bubbles: true, detail: data
        });
    }
}

function getFieldGroup(fieldType) {
    switch (fieldType) {
        case 'text':
        case 'email':
        case 'firstname':
        case 'lastname':
        case 'companyname':
        case 'birthday':
        case 'number':
        case 'tel':
        case 'url':
        case 'date':
        case 'textarea':
        case 'password':
        case 'dropdown':
        case 'signature':
            return 'textFields';
        case 'radio':
            return 'radioFields';
        case 'file':
            return 'fileFields';
        case 'hidden':
            return 'hiddenFields';
        case 'subscribe':
        case 'checkbox':
        case 'captcha':
        case 'gdpr':
            return undefined;
    }
}

class RendererBase {
    render(data, options) {
        const element = this.renderElement(data, options);
        element.dataset['elementJson'] = JSON.stringify(data);
        return element;
    }
    getData(element) {
        const jsonData = element.dataset['elementJson'];
        if (!jsonData) {
            return null;
        }
        try {
            return JSON.parse(jsonData);
        }
        catch (e) {
            console.error('Failed to parse element data:', e);
            return null;
        }
    }
    updateData(element, data) {
        element.dataset['elementJson'] = JSON.stringify(data);
    }
    applyData(element, data) {
        this.updateData(element, data);
        this.applyVisualProperties(element, data);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    applyVisualProperties(element, data) {
    }
    createNew(options) {
        const defaultData = this.createDefaultData();
        return this.render(defaultData, options);
    }
    createWithData(data, options) {
        const clonedData = { ...data, id: DomUtils.generateUniqueElementId([]) };
        return this.render(clonedData, options);
    }
    resetToDefault(element) {
        const currentData = this.getData(element);
        if (!currentData) {
            return;
        }
        const defaultData = { ...this.defaultData, id: currentData.id };
        this.applyData(element, defaultData);
    }
    createDefaultData() {
        const defaultData = this.defaultData;
        defaultData.id = DomUtils.generateUniqueElementId([]);
        return defaultData;
    }
}

class HtmlUtils {
    static { this.config = {
        ALLOWED_TAGS: ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'strong', 'i', 'b', 'ul', 'ol', 'li', 'a', 'br'],
        ALLOWED_ATTR: ['href', 'target', 'class', 'id', 'style', 'title', 'role', 'aria-label', 'src', 'alt'],
        FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'button', 'select', 'option'],
        FORBID_ATTR: [
            'onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onfocus', 'onblur', 'onchange', 'oninput',
            'onsubmit', 'onreset', 'onselect', 'onkeydown', 'onkeyup', 'onkeypress'
        ],
    }; }
    static sanitize(html) {
        return DOMPurify.sanitize(html, this.config);
    }
    static escapeHtml(unsafe) {
        return unsafe
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }
}

class BaseFieldRenderer extends RendererBase {
    renderElement(fieldJsonData, options = {}) {
        const wrapper = document.createElement('div');
        wrapper.id = fieldJsonData.id;
        wrapper.dataset['element'] = 'field';
        const normalizedType = fieldJsonData.type.toLowerCase();
        wrapper.dataset['field'] = normalizedType;
        const group = getFieldGroup(normalizedType);
        if (group) {
            wrapper.dataset['group'] = group;
        }
        if (fieldJsonData.isSystem) {
            wrapper.dataset['system'] = 'true';
        }
        this.applyCustomCssClasses(wrapper, fieldJsonData.cssClasses, options);
        const elementContent = this.renderContent(fieldJsonData, options);
        wrapper.appendChild(elementContent);
        return wrapper;
    }
    renderContent(fieldJsonData, options = {}) {
        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');
        const fieldContainer = document.createElement('div');
        fieldContainer.classList.add('field');
        fieldContainer.classList.toggle('required', fieldJsonData.required === true);
        if (!fieldJsonData.hiddenLabel) {
            const label = this.renderLabel(fieldJsonData);
            fieldContainer.appendChild(label);
        }
        const input = this.renderInput(fieldJsonData, options);
        fieldContainer.appendChild(input);
        const errorContainer = this.renderErrorContainer(fieldJsonData);
        fieldContainer.appendChild(errorContainer);
        if (fieldJsonData.showDescription) {
            const description = this.renderDescription(fieldJsonData);
            fieldContainer.appendChild(description);
        }
        elementContent.appendChild(fieldContainer);
        return elementContent;
    }
    rerender(element, fieldJsonData, options = {}) {
        const oldContent = element.querySelector('.element-content');
        if (oldContent) {
            oldContent.remove();
        }
        const newContent = this.renderContent(fieldJsonData, options);
        element.appendChild(newContent);
        this.updateData(element, fieldJsonData);
    }
    applyVisualProperties(element, data) {
        const oldContent = element.querySelector('.element-content');
        if (oldContent) {
            oldContent.remove();
        }
        const newContent = this.renderContent(data);
        element.appendChild(newContent);
    }
    applyCustomCssClasses(element, cssClasses, options = {}) {
        const isEditor = options.runtimeContext?.mode?.toLocaleLowerCase() === 'editor';
        if (isEditor) {
            return; // we don't add custom classes in the constructor because it may affect the constructor elements.
        }
        if (cssClasses && cssClasses.length > 0) {
            element.classList.add(...cssClasses);
        }
    }
    renderLabel(field) {
        const label = document.createElement('label');
        label.classList.add('label');
        label.setAttribute('for', `field-${field.id}`);
        this.applyHtmlLabel(label, field.label);
        return label;
    }
    applyHtmlLabel(element, label) {
        element.innerHTML = HtmlUtils.sanitize(label || '');
    }
    getPlainLabel(label) {
        const container = document.createElement('div');
        container.innerHTML = HtmlUtils.sanitize(label || '');
        return container.textContent?.trim() || '';
    }
    renderDescription(fieldJsonData) {
        const description = document.createElement('span');
        description.classList.add('description');
        description.id = `description-${fieldJsonData.id}`;
        description.textContent = fieldJsonData.description || '';
        return description;
    }
    renderErrorContainer(fieldJsonData) {
        const errorContainer = document.createElement('div');
        errorContainer.classList.add('fieldJsonData-errors');
        errorContainer.id = `errors-${fieldJsonData.id}`;
        errorContainer.setAttribute('role', 'alert');
        errorContainer.setAttribute('aria-live', 'assertive');
        return errorContainer;
    }
    applyCommonAttributes(input, fieldJsonData) {
        input.id = `field-${fieldJsonData.id}`;
        input.name = fieldJsonData.id;
        if (fieldJsonData.required) {
            input.required = true;
        }
        if (!(input instanceof HTMLSelectElement)) {
            if (fieldJsonData.showPlaceholder && fieldJsonData.placeholder) {
                input.placeholder = fieldJsonData.placeholder;
            }
        }
        const plainLabel = this.getPlainLabel(fieldJsonData.label);
        if (plainLabel) {
            input.dataset['label'] = plainLabel;
        }
        input.setAttribute('aria-required', fieldJsonData.required ? 'true' : 'false');
        input.setAttribute('aria-invalid', 'false');
        if (fieldJsonData.description) {
            input.setAttribute('aria-describedby', `description-${fieldJsonData.id}`);
        }
    }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
class ObjectUtils {
    static getObjValueByStr(obj, str) {
        try {
            str = str.replace(/\[(\w+)\]/g, '.$1');
            str = str.replace(/^\./, '');
            const partsArr = str.split('.');
            for (let i = 0, n = partsArr.length; i < n; ++i) {
                const part = partsArr[i];
                if (part in obj) {
                    obj = obj[part];
                }
                else {
                    return;
                }
            }
            return obj;
        }
        catch (err) {
            return '';
        }
    }
}

/* eslint-disable */
// GENERATED FILE — DO NOT EDIT.
// Source of truth: TruVisibility.TruForms.Resources/FormPages/MetadataFormPageResources.resx (+ .ru.resx).
// Regenerate with: npm run generate-labels (Web.Client) — runs automatically in build-builder.
const GENERATED_DEFAULT_LABELS = {
    en: {
        "SUBMIT_BUTTON_LABEL": "send",
        "PREVIOUS_BUTTON_LABEL": "previous",
        "NEXT_BUTTON_LABEL": "next",
        "THANKFUL_PAGE_HEADER": "Thank you!",
        "THANKFUL_PAGE_TEXT": "Your response has been submitted successfully.",
        "WIDGET_SIGN_IN": "Log In",
        "WIDGET_SIGN_UP": "Sign Up",
        "WIDGET_FORGOT_PASSWORD": "Forgot your password?"
    },
    ru: {
        "SUBMIT_BUTTON_LABEL": "отправить",
        "PREVIOUS_BUTTON_LABEL": "предыдущая",
        "NEXT_BUTTON_LABEL": "следующая",
        "THANKFUL_PAGE_HEADER": "Спасибо!",
        "THANKFUL_PAGE_TEXT": "Ваш ответ успешно отправлен.",
        "WIDGET_SIGN_IN": "Войти",
        "WIDGET_SIGN_UP": "Зарегистрироваться",
        "WIDGET_FORGOT_PASSWORD": "Забыли свой пароль?"
    }
};

class TranslateUtils {
    static { this.en = {
        UPLOAD_FILE: 'Upload File',
        FILE_FIELD: 'File',
        FORM_VALIDATION: {
            MISSING: 'Missing',
            REQUIRED: 'is required',
            FILE_TOO_LARGE: 'The maximum file size should not be more than 28 MB.',
            CAPTCHA: 'Captcha',
            EMAIL: {
                INCORRECT: 'Please enter a valid email address'
            },
            FIRSTNAME: {
                INCORRECT: 'Please enter a valid first name'
            },
            LASTNAME: {
                INCORRECT: 'Please enter a valid last name'
            },
            COMPANYNAME: {
                INCORRECT: 'Please enter a valid company name'
            },
            BIRTHDAY: {
                INCORRECT: 'Please enter a valid birthday'
            },
            SUBSCRIBE: {
                REQUIRED: 'Please select the option'
            },
            URL: {
                INCORRECT: 'Please enter a valid URL (starting with http:// or https://)'
            },
            NUMBER: {
                OVER_MAX_VALUE: 'Value must be no more than',
                OVER_MIN_VALUE: 'Value must be at least',
                INVALID_NUMBER: 'Please enter a valid number'
            },
            TEL: {
                INVALID_CHARACTERS: 'Please enter a valid phone number',
                MIN_DIGITS_COUNT: 'The phone field value is 10 digits minimum',
                MAX_LENGTH: 'The phone field value is 20 symbols maximum'
            },
            DATE: {
                INCOMPLETE_DATE: 'Date entered is incomplete'
            },
            CHECKBOX: {
                MIN_OPTIONS: 'Please select at least one option',
                OVER_MIN_VALUE: 'The minimum number of options is',
                OVER_MAX_VALUE: 'The maximum number of options is',
            },
            RADIO: {
                REQUIRED: 'Please select an option'
            },
            FILE: {
                REQUIRED: 'Please select a file'
            },
            PASSWORD: {
                MISMATCH: 'Passwords do not match'
            },
            GDPR_POLICY_CONSENT: 'agreement with the Privacy Policy'
        },
        EMAIL_FIELD: {
            LABEL: 'Email',
            PLACEHOLDER: 'Enter your email',
        },
        FIRSTNAME_FIELD: {
            LABEL: 'First name',
            PLACEHOLDER: 'Enter your first name',
        },
        LASTNAME_FIELD: {
            LABEL: 'Last name',
            PLACEHOLDER: 'Enter your last name',
        },
        COMPANYNAME_FIELD: {
            LABEL: 'Company name',
            PLACEHOLDER: 'Enter your company name',
        },
        BIRTHDAY_FIELD: {
            LABEL: 'Birthday',
            FORMAT: 'MM/dd/yyyy',
            PLACEHOLDER: 'MM/DD/YYYY'
        },
        SIGNATURE_FIELD: {
            LABEL: 'Signature',
            PLACEHOLDER: 'Sign here',
            CLEAR_BUTTON: 'Clear'
        },
        SUBSCRIBE_FIELD: {
            LABEL: 'Yes, subscribe me to the newsletter.',
        },
        ADDRESS_1_FIELD: {
            LABEL: 'Street Address',
            PLACEHOLDER: 'House number and street name'
        },
        ADDRESS_2_FIELD: {
            LABEL: 'Street Address Line 2'
        },
        CITY_FIELD: {
            LABEL: 'City'
        },
        STATE_FIELD: {
            LABEL: 'State / Province / Region'
        },
        POSTAL_FIELD: {
            LABEL: 'Zip / Postal Code',
        },
        COUNTRY_FIELD: {
            LABEL: 'Country',
        },
        URL_FIELD: {
            LABEL: 'Link',
            DESCRIPTION: 'Enter your link',
            PLACEHOLDER: 'Enter link',
        },
        RADIO_FIELD: {
            LABEL: 'Radio',
            OPTION: 'Option'
        },
        NUMBER_FIELD: {
            LABEL: 'Number',
            PLACEHOLDER: 'Enter a number',
        },
        TEL_FIELD: {
            LABEL: 'Mobile phone',
            PLACEHOLDER: 'Enter your phone',
        },
        TEXT_FIELD: {
            LABEL: 'Short answer',
            PLACEHOLDER: 'Write here...'
        },
        PASSWORD_FIELD: {
            LABEL: 'Password',
            PLACEHOLDER: 'Enter password...',
            CONFIRMATION_LABEL: 'Confirm Password',
            CONFIRMATION_PLACEHOLDER: 'Confirm your password...',
            SHOW_PASSWORD: 'Show password',
            HIDE_PASSWORD: 'Hide password',
            STRENGTH: {
                MIN_LENGTH: 'Use at least 10 characters',
                MIXED_CASE: 'Include uppercase and lowercase english letters',
                HAS_NUMBER: 'Include at least one number'
            }
        },
        TEXTAREA_FIELD: {
            LABEL: 'Long answer',
            PLACEHOLDER: 'Write here...'
        },
        CHECKBOX_FIELD: {
            LABEL: 'Checkbox',
            CHOICE: 'Choice'
        },
        TEXT_WIDGET: {
            THANKFUL_PAGE_HEADER: GENERATED_DEFAULT_LABELS.en.THANKFUL_PAGE_HEADER,
            THANKFUL_PAGE_TEXT: GENERATED_DEFAULT_LABELS.en.THANKFUL_PAGE_TEXT
        },
        SUBMIT_WIDGET: {
            LABEL: GENERATED_DEFAULT_LABELS.en.SUBMIT_BUTTON_LABEL,
            PREVIOUS_LABEL: GENERATED_DEFAULT_LABELS.en.PREVIOUS_BUTTON_LABEL,
            NEXT_LABEL: GENERATED_DEFAULT_LABELS.en.NEXT_BUTTON_LABEL
        },
        SIGN_IN_WIDGET: {
            TEXT: GENERATED_DEFAULT_LABELS.en.WIDGET_SIGN_IN
        },
        SIGN_UP_WIDGET: {
            TEXT: GENERATED_DEFAULT_LABELS.en.WIDGET_SIGN_UP
        },
        FORGOT_PASSWORD_WIDGET: {
            TEXT: GENERATED_DEFAULT_LABELS.en.WIDGET_FORGOT_PASSWORD
        },
        DATE_FIELD: {
            LABEL: 'Date',
            FORMAT: 'MM/dd/yyyy'
        },
        DROPDOWN_FIELD: {
            LABEL: 'Dropdown',
            OPTION: 'Option',
            PLACEHOLDER: 'Select from a list',
        },
        HIDDEN_FIELD: {
            LABEL: 'Hidden variable field',
            SAVE_TO: 'Save to',
            TYPE_DATE: 'Date & Time',
            TYPE_TEXT: 'Text',
            TYPE_NUMBER: 'Number',
            TYPE_EMAIL: 'Email',
            VALUE_EQ: 'Value Equals',
            SUBMISSION_DATE: 'Date & Time Submission'
        },
        ERRORS: {
            CAPTCHA_UNPREPARED: 'Captcha is unprepared',
            VALIDATION_ERROR: 'Validation error',
            INTERNAL_SERVER_ERROR: 'Something went wrong...',
            TRU_CAPTCHA_INVALID_CHALLENGE_ID: 'Invalid TruCAPTCHA ChallengeId',
            TRU_CAPTCHA_MISSED_RESPONSE: 'TruCAPTCHA response is missed',
            TRU_CAPTCHA_INVALID_RESPONSE: 'Invalid TruCAPTCHA response',
            GOOGLE_RECAPTCHA_NOT_CONFIGURED: 'Google reCAPTCHA is not configured',
            GOOGLE_RECAPTCHA_MISSED_RESPONSE: 'Google reCAPTCHA response is missed',
            GOOGLE_RECAPTCHA_INVALID_RESPONSE: 'Invalid Google reCAPTCHA response',
            UNHANDLED_ERROR: 'Something went wrong...',
            FORM_NOT_AVAILABLE: 'Form is not available'
        },
        GDPR: {
            I_ACCEPT: 'I agree to the ',
            AND: ' and the ',
            PRIVACY_POLICY: 'Privacy Policy',
            AGREEMENT_DEFAULT_VOLUME: 'I agree to the Privacy Policy',
            BY_CLICKING_THE: 'By clicking the "',
            YOU_GIVE_US_PERMISSION: '" button, you give us permission to send you marketing and other email communications. You may opt-out at any time.'
        },
        COUNTRY: {
            AF: 'Afghanistan',
            AL: 'Albania',
            DZ: 'Algeria',
            AD: 'Andorra',
            AO: 'Angola',
            AI: 'Anguilla',
            AG: 'Antigua and Barbuda',
            AR: 'Argentina',
            AM: 'Armenia',
            AU: 'Australia',
            AT: 'Austria',
            AZ: 'Azerbaijan',
            BS: 'Bahamas',
            BH: 'Bahrain',
            BD: 'Bangladesh',
            BB: 'Barbados',
            BY: 'Belarus',
            BE: 'Belgium',
            BZ: 'Belize',
            BJ: 'Benin',
            BM: 'Bermuda',
            BT: 'Bhutan',
            BO: 'Bolivia',
            BA: 'Bosnia and Herzegovina',
            BW: 'Botswana',
            BR: 'Brazil',
            IO: 'British Indian Ocean Territory',
            VG: 'British Virgin Islands',
            BN: 'Brunei',
            BG: 'Bulgaria',
            BF: 'Burkina Faso',
            BI: 'Burundi',
            CV: 'Cabo Verde',
            KH: 'Cambodia',
            CM: 'Cameroon',
            CA: 'Canada',
            KY: 'Cayman Islands',
            CF: 'Central African Republic',
            TD: 'Chad',
            CL: 'Chile',
            CO: 'Colombia',
            KM: 'Comoros',
            CK: 'Cook Islands',
            CR: 'Costa Rica',
            HR: 'Croatia',
            CU: 'Cuba',
            CY: 'Cyprus',
            CZ: 'Czech Republic',
            CD: 'Democratic Republic of the Congo',
            DK: 'Denmark',
            DM: 'Dominica',
            DO: 'Dominican Republic',
            EC: 'Ecuador',
            EG: 'Egypt',
            SV: 'El Salvador',
            GQ: 'Equatorial Guinea',
            ER: 'Eritrea',
            EE: 'Estonia',
            ET: 'Ethiopia',
            FK: 'Falkland Islands',
            FO: 'Faroe Islands',
            FM: 'Federated States of Micronesia',
            FJ: 'Fiji',
            FI: 'Finland',
            FR: 'France',
            GA: 'Gabon',
            GM: 'Gambia',
            GE: 'Georgia',
            DE: 'Germany',
            GH: 'Ghana',
            GR: 'Greece',
            GL: 'Greenland',
            GD: 'Grenada',
            GT: 'Guatemala',
            GN: 'Guinea',
            GW: 'Guinea-Bissau',
            GY: 'Guyana',
            HT: 'Haiti',
            HN: 'Honduras',
            HU: 'Hungary',
            IS: 'Iceland',
            IN: 'India',
            ID: 'Indonesia',
            IR: 'Iran',
            IQ: 'Iraq',
            IE: 'Ireland',
            IL: 'Israel',
            IT: 'Italy',
            CI: 'Ivory Coast',
            JM: 'Jamaica',
            JP: 'Japan',
            JE: 'Jersey',
            JO: 'Jordan',
            KZ: 'Kazakhstan',
            KE: 'Kenya',
            KI: 'Kiribati',
            KW: 'Kuwait',
            KG: 'Kyrgyzstan',
            LA: 'Laos',
            LV: 'Latvia',
            LB: 'Lebanon',
            LS: 'Lesotho',
            LR: 'Liberia',
            LY: 'Libya',
            LI: 'Liechtenstein',
            LT: 'Lithuania',
            LU: 'Luxembourg',
            MK: 'Macedonia',
            MG: 'Madagascar',
            MW: 'Malawi',
            MY: 'Malaysia',
            MV: 'Maldives',
            ML: 'Mali',
            MH: 'Marshall Islands',
            MR: 'Mauritania',
            MU: 'Mauritius',
            MX: 'Mexico',
            MD: 'Moldova',
            MN: 'Mongolia',
            ME: 'Montenegro',
            MS: 'Montserrat',
            MA: 'Morocco',
            MZ: 'Mozambique',
            MM: 'Myanmar',
            NA: 'Namibia',
            NR: 'Nauru',
            NP: 'Nepal',
            NZ: 'New Zealand',
            NI: 'Nicaragua',
            NE: 'Niger',
            NG: 'Nigeria',
            NU: 'Niue',
            NF: 'Norfolk Island',
            KP: 'North Korea',
            NO: 'Norway',
            OM: 'Oman',
            PK: 'Pakistan',
            PW: 'Palau',
            PS: 'Palestine',
            PA: 'Panama',
            PG: 'Papua New Guinea',
            PY: 'Paraguay',
            CN: 'People\'s Republic of China',
            PE: 'Peru',
            PH: 'Philippines',
            PN: 'Pitcairn',
            PL: 'Poland',
            PT: 'Portugal',
            QA: 'Qatar',
            TW: 'Republic of China',
            CG: 'Republic of the Congo',
            RO: 'Romania',
            RU: 'Russia',
            RW: 'Rwanda',
            KN: 'Saint Kitts and Nevis',
            LC: 'Saint Lucia',
            VC: 'Saint Vincent and the Grenadines',
            WS: 'Sāmoa',
            SM: 'San Marino',
            ST: 'São Tomé and Príncipe',
            SA: 'Saudi Arabia',
            SN: 'Senegal',
            RS: 'Serbia',
            SC: 'Seychelles',
            SL: 'Sierra Leone',
            SG: 'Singapore',
            SK: 'Slovakia',
            SI: 'Slovenia',
            SB: 'Solomon Islands',
            SO: 'Somalia',
            ZA: 'South Africa',
            GS: 'South Georgia and South Sandwich Islands',
            KR: 'South Korea',
            SS: 'South Sudan',
            ES: 'Spain',
            SD: 'Sudan',
            SR: 'Suriname',
            SZ: 'Swaziland',
            SE: 'Sweden',
            CH: 'Switzerland',
            SY: 'Syria',
            TJ: 'Tajikistan',
            TZ: 'Tanzania',
            TH: 'Thailand',
            NL: 'The Netherlands',
            TG: 'Togo',
            TK: 'Tokelau',
            TO: 'Tonga',
            TT: 'Trinidad and Tobago',
            TN: 'Tunisia',
            TR: 'Turkey',
            TM: 'Turkmenistan',
            TC: 'Turks and Caicos Islands',
            TV: 'Tuvalu',
            UG: 'Uganda',
            UA: 'Ukraine',
            AE: 'United Arab Emirates',
            GB: 'United Kingdom',
            US: 'United States of America',
            UY: 'Uruguay',
            UZ: 'Uzbekistan',
            VU: 'Vanuatu',
            VA: 'Vatican City',
            VE: 'Venezuela',
            VN: 'Vietnam',
            YE: 'Yemen',
            ZM: 'Zambia',
            ZW: 'Zimbabwe'
        }
    }; }
    static { this.ru = {
        UPLOAD_FILE: 'Загрузить файл',
        FILE_FIELD: 'Файл',
        FORM_VALIDATION: {
            MISSING: 'Пропущено',
            REQUIRED: 'обязательно для заполнения',
            FILE_TOO_LARGE: 'Максимальный размер файла не должен быть больше 28 мб.',
            CAPTCHA: 'Капча',
            EMAIL: {
                INCORRECT: 'Введите корректный email адрес'
            },
            FIRSTNAME: {
                INCORRECT: 'Введите корректное имя'
            },
            LASTNAME: {
                INCORRECT: 'Введите корректную фамилию'
            },
            COMPANYNAME: {
                INCORRECT: 'Введите название вашей компании'
            },
            BIRTHDAY: {
                INCORRECT: 'Введите дату вашего рождения'
            },
            SUBSCRIBE: {
                REQUIRED: 'Выберите вариант'
            },
            URL: {
                INCORRECT: 'Введите корректный URL (начиная с http:// или https://)'
            },
            NUMBER: {
                OVER_MAX_VALUE: 'Значение должно быть не более',
                OVER_MIN_VALUE: 'Значение должно быть не менее',
                INVALID_NUMBER: 'Введите корректное число'
            },
            TEL: {
                INVALID_CHARACTERS: 'Введите корректный номер телефона',
                MIN_DIGITS_COUNT: 'Значение поля телефона составляет минимум 10 цифр',
                MAX_LENGTH: 'Значение поля телефона — максимум 20 символов'
            },
            DATE: {
                INCOMPLETE_DATE: 'Дата введена не полностью'
            },
            CHECKBOX: {
                MIN_OPTIONS: 'Выберите хотя бы один вариант',
                OVER_MIN_VALUE: 'Минимальное количество опций',
                OVER_MAX_VALUE: 'Максимальное количество опций',
            },
            RADIO: {
                REQUIRED: 'Выберите один из вариантов'
            },
            FILE: {
                REQUIRED: 'Выберите файл'
            },
            PASSWORD: {
                MISMATCH: 'Пароли не совпадают'
            },
            GDPR_POLICY_CONSENT: 'согласие с политикой конфиденциальности'
        },
        EMAIL_FIELD: {
            LABEL: 'Email',
            PLACEHOLDER: 'Введите ваш email'
        },
        FIRSTNAME_FIELD: {
            LABEL: 'Имя',
            PLACEHOLDER: 'Введите ваше имя'
        },
        LASTNAME_FIELD: {
            LABEL: 'Фамилия',
            PLACEHOLDER: 'Введите вашу фамилию'
        },
        COMPANYNAME_FIELD: {
            LABEL: 'Компания',
            PLACEHOLDER: 'Введите название вашей компании'
        },
        BIRTHDAY_FIELD: {
            LABEL: 'Дата рождения',
            FORMAT: 'dd/MM/yyyy',
            PLACEHOLDER: 'DD/MM/YYYY',
        },
        SIGNATURE_FIELD: {
            LABEL: 'Подпись',
            PLACEHOLDER: 'Распишись здесь',
            CLEAR_BUTTON: 'Очистить'
        },
        SUBSCRIBE_FIELD: {
            LABEL: 'Да, подпишите меня на рассылку.',
        },
        ADDRESS_1_FIELD: {
            LABEL: 'Адрес улицы',
            PLACEHOLDER: 'Номер дома и название улицы'
        },
        ADDRESS_2_FIELD: {
            LABEL: 'Адрес улицы 2'
        },
        CITY_FIELD: {
            LABEL: 'Город'
        },
        STATE_FIELD: {
            LABEL: 'Штат / Провинция / Регион'
        },
        POSTAL_FIELD: {
            LABEL: 'Почтовый индекс',
        },
        COUNTRY_FIELD: {
            LABEL: 'Страна',
        },
        URL_FIELD: {
            LABEL: 'Ссылка',
            DESCRIPTION: 'Введите вашу ссылку',
            PLACEHOLDER: 'Введите ссылку',
        },
        RADIO_FIELD: {
            LABEL: 'Переключатель',
            OPTION: 'Опция'
        },
        TEXT_FIELD: {
            LABEL: 'Короткий ответ',
            PLACEHOLDER: 'Напишите здесь...'
        },
        PASSWORD_FIELD: {
            LABEL: 'Пароль',
            PLACEHOLDER: 'Введите пароль...',
            CONFIRMATION_LABEL: 'Подтвердите пароль',
            CONFIRMATION_PLACEHOLDER: 'Подтвердите пароль...',
            SHOW_PASSWORD: 'Показать пароль',
            HIDE_PASSWORD: 'Скрыть пароль',
            STRENGTH: {
                MIN_LENGTH: 'Используйте минимум 10 символов',
                MIXED_CASE: 'Используйте заглавные и строчные английские буквы',
                HAS_NUMBER: 'Используйте хотя бы одну цифру'
            }
        },
        TEXTAREA_FIELD: {
            LABEL: 'Длинный ответ',
            PLACEHOLDER: 'Напишите здесь...'
        },
        NUMBER_FIELD: {
            LABEL: 'Число',
            PLACEHOLDER: 'Введите число',
        },
        TEL_FIELD: {
            LABEL: 'Телефон',
            PLACEHOLDER: 'Введите ваш номер телефона',
        },
        CHECKBOX_FIELD: {
            LABEL: 'Чекбокс',
            CHOICE: 'Вариант'
        },
        TEXT_WIDGET: {
            THANKFUL_PAGE_HEADER: GENERATED_DEFAULT_LABELS.ru.THANKFUL_PAGE_HEADER,
            THANKFUL_PAGE_TEXT: GENERATED_DEFAULT_LABELS.ru.THANKFUL_PAGE_TEXT
        },
        SUBMIT_WIDGET: {
            LABEL: GENERATED_DEFAULT_LABELS.ru.SUBMIT_BUTTON_LABEL,
            PREVIOUS_LABEL: GENERATED_DEFAULT_LABELS.ru.PREVIOUS_BUTTON_LABEL,
            NEXT_LABEL: GENERATED_DEFAULT_LABELS.ru.NEXT_BUTTON_LABEL
        },
        SIGN_IN_WIDGET: {
            TEXT: GENERATED_DEFAULT_LABELS.ru.WIDGET_SIGN_IN
        },
        SIGN_UP_WIDGET: {
            TEXT: GENERATED_DEFAULT_LABELS.ru.WIDGET_SIGN_UP
        },
        FORGOT_PASSWORD_WIDGET: {
            TEXT: GENERATED_DEFAULT_LABELS.ru.WIDGET_FORGOT_PASSWORD
        },
        DATE_FIELD: {
            LABEL: 'Дата',
            FORMAT: 'dd/MM/yyyy'
        },
        DROPDOWN_FIELD: {
            LABEL: 'Выпадающий список',
            OPTION: 'Опция',
            PLACEHOLDER: 'Выбрать из списка',
        },
        HIDDEN_FIELD: {
            LABEL: 'Скрытая переменная',
            SAVE_TO: 'Сохранить в',
            TYPE_DATE: 'Дата и время',
            TYPE_TEXT: 'Текст',
            TYPE_NUMBER: 'Число',
            TYPE_EMAIL: 'Email',
            VALUE_EQ: 'Значение равно',
            SUBMISSION_DATE: 'Дата и время отправки формы'
        },
        ERRORS: {
            CAPTCHA_UNPREPARED: 'Капча не подготовлена',
            VALIDATION_ERROR: 'Ошибка валидации',
            INTERNAL_SERVER_ERROR: 'Что-то пошло не так...',
            TRU_CAPTCHA_INVALID_CHALLENGE_ID: 'Недопустимый ChallengeId для TruCAPTCHA',
            TRU_CAPTCHA_MISSED_RESPONSE: 'Пропущен ответ TruCAPTCHA',
            TRU_CAPTCHA_INVALID_RESPONSE: 'Недопустимый ответ TruCAPTCHA',
            GOOGLE_RECAPTCHA_NOT_CONFIGURED: 'Google reCAPTCHA не настроена',
            GOOGLE_RECAPTCHA_MISSED_RESPONSE: 'Пропущен ответ Google reCAPTCHA',
            GOOGLE_RECAPTCHA_INVALID_RESPONSE: 'Недопустимый ответ Google reCAPTCHA',
            UNHANDLED_ERROR: 'Что-то пошло не так...',
            FORM_NOT_AVAILABLE: 'Форма недоступна'
        },
        GDPR: {
            I_ACCEPT: 'Я согласен(-на) с ',
            AND: ' и ',
            PRIVACY_POLICY: 'Политикой конфиденциальности',
            AGREEMENT_DEFAULT_VOLUME: 'Я согласен(-на) с Политикой конфиденциальности',
            BY_CLICKING_THE: 'Нажимая кнопку "',
            YOU_GIVE_US_PERMISSION: '", вы даете нам разрешение на отправку вам маркетинговых и других сообщений по электронной почте. Вы можете отказаться в любое время.'
        },
        COUNTRY: {
            AU: 'Австралия',
            AT: 'Австрия',
            AZ: 'Азербайджан',
            AL: 'Албания',
            DZ: 'Алжир',
            AI: 'Ангилья',
            AO: 'Ангола',
            AD: 'Андорра',
            AG: 'Антигуа и Барбуда',
            AR: 'Аргентина',
            AM: 'Армения',
            AF: 'Афганистан',
            BS: 'Багамы',
            BD: 'Бангладеш',
            BB: 'Барбадос',
            BH: 'Бахрейн',
            BY: 'Беларусь',
            BZ: 'Белиз',
            BE: 'Бельгия',
            BJ: 'Бенин',
            BM: 'Бермуды',
            BG: 'Болгария',
            BO: 'Боливия',
            BA: 'Босния и Герцеговина',
            BW: 'Ботсвана',
            BR: 'Бразилия',
            IO: 'Британская Территория в Индийском Океане',
            BN: 'Бруней',
            BF: 'Буркина-Фасо',
            BI: 'Бурунди',
            BT: 'Бутан',
            VU: 'Вануату',
            VA: 'Ватикан',
            GB: 'Великобритания',
            HU: 'Венгрия',
            VE: 'Венесуэла',
            VG: 'Виргинские Острова (Брит.)',
            VN: 'Вьетнам',
            GA: 'Габон',
            HT: 'Гаити',
            GY: 'Гайана',
            GM: 'Гамбия',
            GH: 'Гана',
            GT: 'Гватемала',
            GN: 'Гвинея',
            GW: 'Гвинея-Бисау',
            DE: 'Германия',
            HN: 'Гондурас',
            GD: 'Гренада',
            GL: 'Гренландия',
            GR: 'Греция',
            GE: 'Грузия',
            DK: 'Дания',
            CD: 'Демократическая Республика Конго',
            JE: 'Джерси',
            DM: 'Доминика',
            DO: 'Доминиканская Республика',
            EG: 'Египет',
            ZM: 'Замбия',
            ZW: 'Зимбабве',
            IL: 'Израиль',
            IN: 'Индия',
            ID: 'Индонезия',
            JO: 'Иордания',
            IQ: 'Ирак',
            IR: 'Иран',
            IE: 'Ирландия',
            IS: 'Исландия',
            ES: 'Испания',
            IT: 'Италия',
            YE: 'Йемен',
            CV: 'Кабо-Верде',
            KZ: 'Казахстан',
            KH: 'Камбоджа',
            CM: 'Камерун',
            CA: 'Канада',
            QA: 'Катар',
            KE: 'Кения',
            KG: 'Киргизия',
            KI: 'Кирибати',
            CN: 'Китайская Народная Республика',
            TW: 'Китайская Республика',
            CO: 'Колумбия',
            KM: 'Коморы',
            CG: 'Конго',
            KP: 'Корейская Народно-Демократическая Республика',
            CR: 'Коста-Рика',
            CI: 'Кот-д’Ивуар',
            CU: 'Куба',
            KW: 'Кувейт',
            LA: 'Лаос',
            LV: 'Латвия',
            LS: 'Лесото',
            LR: 'Либерия',
            LB: 'Ливан',
            LY: 'Ливия',
            LT: 'Литва',
            LI: 'Лихтенштейн',
            LU: 'Люксембург',
            MU: 'Маврикий',
            MR: 'Мавритания',
            MG: 'Мадагаскар',
            MK: 'Македония',
            MW: 'Малави',
            MY: 'Малайзия',
            ML: 'Мали',
            MV: 'Мальдивы',
            MA: 'Марокко',
            MH: 'Маршалловы Острова',
            MX: 'Мексика',
            MZ: 'Мозамбик',
            MD: 'Молдова',
            MN: 'Монголия',
            MS: 'Монтсеррат',
            MM: 'Мьянма',
            NA: 'Намибия',
            NR: 'Науру',
            NP: 'Непал',
            NE: 'Нигер',
            NG: 'Нигерия',
            NL: 'Нидерланды',
            NI: 'Никарагуа',
            NU: 'Ниуэ',
            NZ: 'Новая Зеландия',
            NO: 'Норвегия',
            AE: 'Объединённые Арабские Эмираты',
            OM: 'Оман',
            NF: 'Остров Норфолк',
            KY: 'Острова Кайман',
            CK: 'Острова Кука',
            TC: 'Острова Тёркс и Кайкос',
            PK: 'Пакистан',
            PW: 'Палау',
            PS: 'Палестина',
            PA: 'Панама',
            PG: 'Папуа — Новая Гвинея',
            PY: 'Парагвай',
            PE: 'Перу',
            PN: 'Питкэрн',
            PL: 'Польша',
            PT: 'Португалия',
            CY: 'Республика Кипр',
            KR: 'Республика Корея',
            RU: 'Россия',
            RW: 'Руанда',
            RO: 'Румыния',
            SV: 'Сальвадор',
            WS: 'Самоа',
            SM: 'Сан-Марино',
            ST: 'Сан-Томе и Принсипи',
            SA: 'Саудовская Аравия',
            SZ: 'Свазиленд',
            SC: 'Сейшелы',
            SN: 'Сенегал',
            VC: 'Сент-Винсент и Гренадины',
            KN: 'Сент-Китс и Невис',
            LC: 'Сент-Люсия',
            RS: 'Сербия',
            SG: 'Сингапур',
            SY: 'Сирия',
            SK: 'Словакия',
            SI: 'Словения',
            US: 'Соединённые Штаты Америки',
            SB: 'Соломоновы Острова',
            SO: 'Сомали',
            SD: 'Судан',
            SR: 'Суринам',
            SL: 'Сьерра-Леоне',
            TJ: 'Таджикистан',
            TH: 'Таиланд',
            TZ: 'Танзания',
            TG: 'Того',
            TK: 'Токелау',
            TO: 'Тонга',
            TT: 'Тринидад и Тобаго',
            TV: 'Тувалу',
            TN: 'Тунис',
            TM: 'Туркмения',
            TR: 'Турция',
            UG: 'Уганда',
            UZ: 'Узбекистан',
            UA: 'Украина',
            UY: 'Уругвай',
            FO: 'Фарерские Острова',
            FM: 'Федеративные Штаты Микронезии',
            FJ: 'Фиджи',
            PH: 'Филиппины',
            FI: 'Финляндия',
            FK: 'Фолклендские Oстрова',
            FR: 'Франция',
            HR: 'Хорватия',
            CF: 'Центральноафриканская Республика',
            TD: 'Чад',
            ME: 'Черногория',
            CZ: 'Чехия',
            CL: 'Чили',
            CH: 'Швейцария',
            SE: 'Швеция',
            EC: 'Эквадор',
            GQ: 'Экваториальная Гвинея',
            ER: 'Эритрея',
            EE: 'Эстония',
            ET: 'Эфиопия',
            GS: 'Южная Георгия и Южные Сандвичевы Острова',
            ZA: 'Южно-Африканская Республика',
            SS: 'Южный Судан',
            JM: 'Ямайка',
            JP: 'Япония'
        }
    }; }
    static get UPLOAD_FILE() { return TranslateUtils.getTranslation('UPLOAD_FILE'); }
    static get FILE_FIELD() { return TranslateUtils.getTranslation('FILE_FIELD'); }
    static get FORM_VALIDATION() {
        return {
            MISSING: TranslateUtils.getTranslation('FORM_VALIDATION', 'MISSING'),
            REQUIRED: TranslateUtils.getTranslation('FORM_VALIDATION', 'REQUIRED'),
            FILE_TOO_LARGE: TranslateUtils.getTranslation('FORM_VALIDATION', 'FILE_TOO_LARGE'),
            CAPTCHA: TranslateUtils.getTranslation('FORM_VALIDATION', 'CAPTCHA'),
            EMAIL: {
                INCORRECT: TranslateUtils.getTranslation('FORM_VALIDATION', 'EMAIL', 'INCORRECT')
            },
            FIRSTNAME: {
                INCORRECT: TranslateUtils.getTranslation('FORM_VALIDATION', 'FIRSTNAME', 'INCORRECT')
            },
            LASTNAME: {
                INCORRECT: TranslateUtils.getTranslation('FORM_VALIDATION', 'LASTNAME', 'INCORRECT')
            },
            COMPANYNAME: {
                INCORRECT: TranslateUtils.getTranslation('FORM_VALIDATION', 'COMPANYNAME', 'INCORRECT')
            },
            BIRTHDAY: {
                INCORRECT: TranslateUtils.getTranslation('FORM_VALIDATION', 'BIRTHDAY', 'INCORRECT')
            },
            SUBSCRIBE: {
                REQUIRED: TranslateUtils.getTranslation('FORM_VALIDATION', 'SUBSCRIBE', 'REQUIRED')
            },
            URL: {
                INCORRECT: TranslateUtils.getTranslation('FORM_VALIDATION', 'URL', 'INCORRECT')
            },
            NUMBER: {
                OVER_MAX_VALUE: TranslateUtils.getTranslation('FORM_VALIDATION', 'NUMBER', 'OVER_MAX_VALUE'),
                OVER_MIN_VALUE: TranslateUtils.getTranslation('FORM_VALIDATION', 'NUMBER', 'OVER_MIN_VALUE'),
                INVALID_NUMBER: TranslateUtils.getTranslation('FORM_VALIDATION', 'NUMBER', 'INVALID_NUMBER'),
            },
            TEL: {
                INVALID_CHARACTERS: TranslateUtils.getTranslation('FORM_VALIDATION', 'TEL', 'INVALID_CHARACTERS'),
                MIN_DIGITS_COUNT: TranslateUtils.getTranslation('FORM_VALIDATION', 'TEL', 'MIN_DIGITS_COUNT'),
                MAX_LENGTH: TranslateUtils.getTranslation('FORM_VALIDATION', 'TEL', 'MAX_LENGTH'),
            },
            DATE: {
                INCOMPLETE_DATE: TranslateUtils.getTranslation('FORM_VALIDATION', 'DATE', 'INCOMPLETE_DATE')
            },
            CHECKBOX: {
                MIN_OPTIONS: TranslateUtils.getTranslation('FORM_VALIDATION', 'CHECKBOX', 'MIN_OPTIONS'),
                OVER_MIN_VALUE: TranslateUtils.getTranslation('FORM_VALIDATION', 'CHECKBOX', 'OVER_MIN_VALUE'),
                OVER_MAX_VALUE: TranslateUtils.getTranslation('FORM_VALIDATION', 'CHECKBOX', 'OVER_MAX_VALUE'),
            },
            RADIO: {
                REQUIRED: TranslateUtils.getTranslation('FORM_VALIDATION', 'RADIO', 'REQUIRED')
            },
            FILE: {
                REQUIRED: TranslateUtils.getTranslation('FORM_VALIDATION', 'FILE', 'REQUIRED')
            },
            PASSWORD: {
                MISMATCH: TranslateUtils.getTranslation('FORM_VALIDATION', 'PASSWORD', 'MISMATCH')
            },
            GDPR_POLICY_CONSENT: TranslateUtils.getTranslation('FORM_VALIDATION', 'GDPR_POLICY_CONSENT'),
        };
    }
    static get HIDDEN_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('HIDDEN_FIELD', 'LABEL'),
            SAVE_TO: TranslateUtils.getTranslation('HIDDEN_FIELD', 'SAVE_TO'),
            TYPE_DATE: TranslateUtils.getTranslation('HIDDEN_FIELD', 'TYPE_DATE'),
            TYPE_TEXT: TranslateUtils.getTranslation('HIDDEN_FIELD', 'TYPE_TEXT'),
            TYPE_NUMBER: TranslateUtils.getTranslation('HIDDEN_FIELD', 'TYPE_NUMBER'),
            TYPE_EMAIL: TranslateUtils.getTranslation('HIDDEN_FIELD', 'TYPE_EMAIL'),
            VALUE_EQ: TranslateUtils.getTranslation('HIDDEN_FIELD', 'VALUE_EQ'),
            SUBMISSION_DATE: TranslateUtils.getTranslation('HIDDEN_FIELD', 'SUBMISSION_DATE')
        };
    }
    static get RADIO_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('RADIO_FIELD.LABEL'),
            OPTION: TranslateUtils.getTranslation('RADIO_FIELD.OPTION')
        };
    }
    static get TEXT_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('TEXT_FIELD.LABEL'),
            PLACEHOLDER: TranslateUtils.getTranslation('TEXT_FIELD.PLACEHOLDER')
        };
    }
    static get PASSWORD_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('PASSWORD_FIELD.LABEL'),
            PLACEHOLDER: TranslateUtils.getTranslation('PASSWORD_FIELD.PLACEHOLDER'),
            CONFIRMATION_LABEL: TranslateUtils.getTranslation('PASSWORD_FIELD.CONFIRMATION_LABEL'),
            CONFIRMATION_PLACEHOLDER: TranslateUtils.getTranslation('PASSWORD_FIELD.CONFIRMATION_PLACEHOLDER'),
            SHOW_PASSWORD: TranslateUtils.getTranslation('PASSWORD_FIELD.SHOW_PASSWORD'),
            HIDE_PASSWORD: TranslateUtils.getTranslation('PASSWORD_FIELD.HIDE_PASSWORD'),
            STRENGTH: {
                MIN_LENGTH: TranslateUtils.getTranslation('PASSWORD_FIELD.STRENGTH.MIN_LENGTH'),
                MIXED_CASE: TranslateUtils.getTranslation('PASSWORD_FIELD.STRENGTH.MIXED_CASE'),
                HAS_NUMBER: TranslateUtils.getTranslation('PASSWORD_FIELD.STRENGTH.HAS_NUMBER')
            }
        };
    }
    static get TEXTAREA_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('TEXTAREA_FIELD.LABEL'),
            PLACEHOLDER: TranslateUtils.getTranslation('TEXTAREA_FIELD.PLACEHOLDER')
        };
    }
    static get NUMBER_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('NUMBER_FIELD', 'LABEL'),
            PLACEHOLDER: TranslateUtils.getTranslation('NUMBER_FIELD', 'PLACEHOLDER'),
        };
    }
    static get TEL_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('TEL_FIELD', 'LABEL'),
            PLACEHOLDER: TranslateUtils.getTranslation('TEL_FIELD', 'PLACEHOLDER'),
        };
    }
    static get EMAIL_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('EMAIL_FIELD', 'LABEL'),
            PLACEHOLDER: TranslateUtils.getTranslation('EMAIL_FIELD', 'PLACEHOLDER'),
        };
    }
    static get FIRSTNAME_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('FIRSTNAME_FIELD', 'LABEL'),
            PLACEHOLDER: TranslateUtils.getTranslation('FIRSTNAME_FIELD', 'PLACEHOLDER'),
        };
    }
    static get LASTNAME_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('LASTNAME_FIELD', 'LABEL'),
            PLACEHOLDER: TranslateUtils.getTranslation('LASTNAME_FIELD', 'PLACEHOLDER'),
        };
    }
    static get COMPANYNAME_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('COMPANYNAME_FIELD', 'LABEL'),
            PLACEHOLDER: TranslateUtils.getTranslation('COMPANYNAME_FIELD', 'PLACEHOLDER'),
        };
    }
    static get BIRTHDAY_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('BIRTHDAY_FIELD', 'LABEL'),
            FORMAT: TranslateUtils.getTranslation('BIRTHDAY_FIELD', 'FORMAT'),
            PLACEHOLDER: TranslateUtils.getTranslation('BIRTHDAY_FIELD', 'PLACEHOLDER'),
        };
    }
    static get SIGNATURE_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('SIGNATURE_FIELD', 'LABEL'),
            PLACEHOLDER: TranslateUtils.getTranslation('SIGNATURE_FIELD', 'PLACEHOLDER'),
            CLEAR_BUTTON: TranslateUtils.getTranslation('SIGNATURE_FIELD', 'CLEAR_BUTTON'),
        };
    }
    static get SUBSCRIBE_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('SUBSCRIBE_FIELD', 'LABEL'),
        };
    }
    static get ADDRESS_1_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('ADDRESS_1_FIELD', 'LABEL'),
            PLACEHOLDER: TranslateUtils.getTranslation('ADDRESS_1_FIELD', 'PLACEHOLDER'),
        };
    }
    static get ADDRESS_2_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('ADDRESS_2_FIELD', 'LABEL'),
        };
    }
    static get CITY_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('CITY_FIELD', 'LABEL'),
        };
    }
    static get STATE_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('STATE_FIELD', 'LABEL'),
        };
    }
    static get POSTAL_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('POSTAL_FIELD', 'LABEL'),
        };
    }
    static get COUNTRY_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('COUNTRY_FIELD', 'LABEL'),
        };
    }
    static get URL_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('URL_FIELD', 'LABEL'),
            PLACEHOLDER: TranslateUtils.getTranslation('URL_FIELD', 'PLACEHOLDER'),
        };
    }
    static get CHECKBOX_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('CHECKBOX_FIELD', 'LABEL'),
            CHOICE: TranslateUtils.getTranslation('CHECKBOX_FIELD', 'CHOICE'),
        };
    }
    static get TEXT_WIDGET() {
        return {
            THANKFUL_PAGE_HEADER: TranslateUtils.getTranslation('TEXT_WIDGET', 'THANKFUL_PAGE_HEADER'),
            THANKFUL_PAGE_TEXT: TranslateUtils.getTranslation('TEXT_WIDGET', 'THANKFUL_PAGE_TEXT')
        };
    }
    static get SUBMIT_WIDGET() {
        return {
            LABEL: TranslateUtils.getTranslation('SUBMIT_WIDGET', 'LABEL'),
            PREVIOUS_LABEL: TranslateUtils.getTranslation('SUBMIT_WIDGET', 'PREVIOUS_LABEL'),
            NEXT_LABEL: TranslateUtils.getTranslation('SUBMIT_WIDGET', 'NEXT_LABEL')
        };
    }
    static get SIGN_IN_WIDGET() {
        return {
            TEXT: TranslateUtils.getTranslation('SIGN_IN_WIDGET', 'TEXT')
        };
    }
    static get SIGN_UP_WIDGET() {
        return {
            TEXT: TranslateUtils.getTranslation('SIGN_UP_WIDGET', 'TEXT')
        };
    }
    static get FORGOT_PASSWORD_WIDGET() {
        return {
            TEXT: TranslateUtils.getTranslation('FORGOT_PASSWORD_WIDGET', 'TEXT')
        };
    }
    static get DATE_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('DATE_FIELD.LABEL'),
            FORMAT: TranslateUtils.getTranslation('DATE_FIELD.FORMAT')
        };
    }
    static get DROPDOWN_FIELD() {
        return {
            LABEL: TranslateUtils.getTranslation('DROPDOWN_FIELD.LABEL'),
            OPTION: TranslateUtils.getTranslation('DROPDOWN_FIELD.OPTION'),
            PLACEHOLDER: TranslateUtils.getTranslation('DROPDOWN_FIELD', 'PLACEHOLDER'),
        };
    }
    static get ERRORS() {
        return {
            CAPTCHA_UNPREPARED: TranslateUtils.getTranslation('ERRORS', 'CAPTCHA_UNPREPARED'),
            VALIDATION_ERROR: TranslateUtils.getTranslation('ERRORS', 'VALIDATION_ERROR'),
            INTERNAL_SERVER_ERROR: TranslateUtils.getTranslation('ERRORS', 'INTERNAL_SERVER_ERROR'),
            TRU_CAPTCHA_INVALID_CHALLENGE_ID: TranslateUtils.getTranslation('ERRORS', 'TRU_CAPTCHA_INVALID_CHALLENGE_ID'),
            TRU_CAPTCHA_MISSED_RESPONSE: TranslateUtils.getTranslation('ERRORS', 'TRU_CAPTCHA_MISSED_RESPONSE'),
            TRU_CAPTCHA_INVALID_RESPONSE: TranslateUtils.getTranslation('ERRORS', 'TRU_CAPTCHA_INVALID_RESPONSE'),
            GOOGLE_RECAPTCHA_NOT_CONFIGURED: TranslateUtils.getTranslation('ERRORS', 'GOOGLE_RECAPTCHA_NOT_CONFIGURED'),
            GOOGLE_RECAPTCHA_MISSED_RESPONSE: TranslateUtils.getTranslation('ERRORS', 'GOOGLE_RECAPTCHA_MISSED_RESPONSE'),
            GOOGLE_RECAPTCHA_INVALID_RESPONSE: TranslateUtils.getTranslation('ERRORS', 'GOOGLE_RECAPTCHA_INVALID_RESPONSE'),
            UNHANDLED_ERROR: TranslateUtils.getTranslation('ERRORS', 'UNHANDLED_ERROR'),
            FORM_NOT_AVAILABLE: TranslateUtils.getTranslation('ERRORS', 'FORM_NOT_AVAILABLE'),
        };
    }
    static get GDPR() {
        return {
            I_ACCEPT: TranslateUtils.getTranslation('GDPR', 'I_ACCEPT'),
            AND: TranslateUtils.getTranslation('GDPR', 'AND'),
            PRIVACY_POLICY: TranslateUtils.getTranslation('GDPR', 'PRIVACY_POLICY'),
            AGREEMENT_DEFAULT_VOLUME: TranslateUtils.getTranslation('GDPR', 'AGREEMENT_DEFAULT_VOLUME'),
            BY_CLICKING_THE: TranslateUtils.getTranslation('GDPR', 'BY_CLICKING_THE'),
            YOU_GIVE_US_PERMISSION: TranslateUtils.getTranslation('GDPR', 'YOU_GIVE_US_PERMISSION'),
        };
    }
    static get COUNTRY() {
        const countryData = document.documentElement.lang === 'ru'
            ? TranslateUtils.ru.COUNTRY
            : TranslateUtils.en.COUNTRY;
        return Object.keys(countryData).reduce((acc, key) => {
            acc[key] = countryData[key];
            return acc;
        }, {});
    }
    static getTranslation(...keys) {
        const obj = document.documentElement.lang === 'ru' ? TranslateUtils.ru : TranslateUtils.en;
        const result = ObjectUtils.getObjValueByStr(obj, keys.join('.'));
        return typeof result === 'string' ? result : '';
    }
    static prepareKey(key) {
        return key.replaceAll('-', '_').toLocaleUpperCase();
    }
}

class Base64Utils {
    static serialize(object) {
        if (!object) {
            return null;
        }
        const json = JSON.stringify(object);
        return this.utf8ToBase64(json);
    }
    static deserialize(object) {
        if (!object) {
            return {};
        }
        const str = this.base64ToUtf8(object);
        return JSON.parse(str);
    }
    static utf8ToBase64(str) {
        return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function (_, p1) {
            return String.fromCharCode(parseInt(p1, 16));
        }));
    }
    static base64ToUtf8(str) {
        return decodeURIComponent(Array.prototype.map.call(atob(str), function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
    }
}

class HiddenFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'hidden',
            value: '',
            dataType: 'text',
            label: ''
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderElement(fieldJsonData, options = {}) {
        const wrapper = document.createElement('div');
        wrapper.id = fieldJsonData.id;
        wrapper.setAttribute('data-group', 'hiddenFields');
        wrapper.setAttribute('data-element', 'hidden');
        if (fieldJsonData.label) {
            wrapper.setAttribute('data-label', fieldJsonData.label);
        }
        if (fieldJsonData.crmFieldData) {
            wrapper.setAttribute('data-crm-field', Base64Utils.serialize(fieldJsonData.crmFieldData) ?? '');
        }
        const inputId = DomUtils.generateUniqueElementId([]);
        const input = document.createElement('input');
        input.id = inputId;
        input.name = fieldJsonData.id;
        input.type = 'hidden';
        input.value = fieldJsonData.value || '';
        input.setAttribute('data-type', fieldJsonData.dataType || 'text');
        wrapper.appendChild(input);
        wrapper.appendChild(this.createBuilderInfo(fieldJsonData));
        return wrapper;
    }
    applyVisualProperties(element, data) {
        if (data.label) {
            element.setAttribute('data-label', data.label);
        }
        else {
            element.removeAttribute('data-label');
        }
        if (data.crmFieldData) {
            element.setAttribute('data-crm-field', Base64Utils.serialize(data.crmFieldData) ?? '');
        }
        else {
            element.removeAttribute('data-crm-field');
        }
        const input = element.querySelector('input[type="hidden"]');
        if (input) {
            input.value = data.value || '';
            input.setAttribute('data-type', data.dataType || 'text');
        }
        this.updateBuilderInfo(element, data);
    }
    updateBuilderInfo(element, fieldJsonData) {
        let builderInfo = element.querySelector('.field-info');
        if (!builderInfo) {
            builderInfo = this.createBuilderInfo(fieldJsonData);
            element.appendChild(builderInfo);
            return;
        }
        const titleElement = builderInfo.querySelector('.field-title');
        if (titleElement) {
            titleElement.textContent = fieldJsonData.label || TranslateUtils.HIDDEN_FIELD.LABEL;
        }
        const typeElement = builderInfo.querySelector('.field-type');
        if (typeElement) {
            let typeText = TranslateUtils.HIDDEN_FIELD.TYPE_TEXT;
            switch (fieldJsonData.dataType) {
                case 'date':
                    typeText = TranslateUtils.HIDDEN_FIELD.TYPE_DATE;
                    break;
                case 'number':
                    typeText = TranslateUtils.HIDDEN_FIELD.TYPE_NUMBER;
                    break;
                case 'email':
                    typeText = TranslateUtils.HIDDEN_FIELD.TYPE_EMAIL;
                    break;
            }
            typeElement.textContent = `${typeText}. ${TranslateUtils.HIDDEN_FIELD.SAVE_TO}: ${fieldJsonData.crmFieldData?.name ?? '-'}`;
        }
        const valueElement = builderInfo.querySelector('.field-value');
        if (valueElement) {
            if (fieldJsonData.dataType === 'date' && fieldJsonData.value === HiddenFieldTags.SubmissionDate) {
                valueElement.textContent = `${TranslateUtils.HIDDEN_FIELD.VALUE_EQ}: ${TranslateUtils.HIDDEN_FIELD.SUBMISSION_DATE}`;
            }
            else {
                valueElement.textContent = `${TranslateUtils.HIDDEN_FIELD.VALUE_EQ}: ${fieldJsonData.value ?? ''}`;
            }
        }
    }
    createBuilderInfo(fieldJsonData) {
        const builderInfo = document.createElement('div');
        builderInfo.className = 'element-builder-only field-info';
        const headline = document.createElement('div');
        headline.className = 'line field-headline';
        headline.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="field-icon">
                <g clip-path="url(#clip0_6602_30165)">
                <path d="M7.15625 3.38401C8.70917 3.19895 10.28 3.52723 11.6288 4.31871C12.9777 5.1102 14.0304 6.3214 14.6263 7.76735C14.6818 7.91703 14.6818 8.08167 14.6263 8.23135C14.3812 8.82535 14.0575 9.38368 13.6636 9.89135" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9.38942 9.43866C9.01222 9.80297 8.50702 10.0046 7.98262 10C7.45823 9.99545 6.9566 9.78511 6.58579 9.41429C6.21497 9.04348 6.00463 8.54185 6.00008 8.01746C5.99552 7.49307 6.19711 6.98786 6.56142 6.61066" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.6517 11.666C10.7674 12.1899 9.78068 12.5174 8.75861 12.6263C7.73654 12.7352 6.703 12.623 5.72812 12.2973C4.75324 11.9716 3.85983 11.4399 3.10851 10.7385C2.35719 10.0371 1.76554 9.18226 1.3737 8.23202C1.31814 8.08235 1.31814 7.9177 1.3737 7.76802C1.96479 6.33459 3.00482 5.13152 4.3377 4.33936" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M1.33398 1.33331L14.6673 14.6666" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                <clipPath id="clip0_6602_30165">
                <rect width="16" height="16" fill="white"/>
                </clipPath>
                </defs>
            </svg>
            <span class="field-title">${fieldJsonData.label || TranslateUtils.HIDDEN_FIELD.LABEL}</span>
        `;
        builderInfo.appendChild(headline);
        const typeLine = document.createElement('div');
        typeLine.className = 'line field-type';
        let typeText = TranslateUtils.HIDDEN_FIELD.TYPE_TEXT;
        switch (fieldJsonData.dataType) {
            case 'date':
                typeText = TranslateUtils.HIDDEN_FIELD.TYPE_DATE;
                break;
            case 'number':
                typeText = TranslateUtils.HIDDEN_FIELD.TYPE_NUMBER;
                break;
            case 'email':
                typeText = TranslateUtils.HIDDEN_FIELD.TYPE_EMAIL;
                break;
        }
        typeLine.textContent = `${typeText}. ${TranslateUtils.HIDDEN_FIELD.SAVE_TO}: ${fieldJsonData.crmFieldData?.name ?? '-'}`;
        builderInfo.appendChild(typeLine);
        const valueLine = document.createElement('div');
        valueLine.className = 'line field-value';
        if (fieldJsonData.dataType === 'date' && fieldJsonData.value === HiddenFieldTags.SubmissionDate) {
            valueLine.textContent = `${TranslateUtils.HIDDEN_FIELD.VALUE_EQ}: ${TranslateUtils.HIDDEN_FIELD.SUBMISSION_DATE}`;
        }
        else {
            valueLine.textContent = `${TranslateUtils.HIDDEN_FIELD.VALUE_EQ}: ${fieldJsonData.value ?? ''}`;
        }
        builderInfo.appendChild(valueLine);
        return builderInfo;
    }
    renderContent() {
        return document.createElement('div');
    }
    renderInput() {
        return document.createElement('div');
    }
}

const validHiddenTypes = ['hidden'];
var HiddenFieldTags;
(function (HiddenFieldTags) {
    HiddenFieldTags["SubmissionDate"] = "*|submission_date|*";
})(HiddenFieldTags || (HiddenFieldTags = {}));
class HiddenFieldBlock extends BaseBuilderEntity {
    constructor() {
        super(...arguments);
        this.elementsMap = new Map();
        this.renderer = new HiddenFieldRenderer();
    }
    get elements() {
        return Array.from(this.elementsMap.values());
    }
    get isEmpty() {
        return this.htmlElement.childElementCount === 0;
    }
    getElement(elementId) {
        return this.elementsMap.get(elementId) ?? null;
    }
    addElement(data) {
        const id = data?.id || DomUtils.generateUniqueElementId([]);
        const fieldData = {
            id,
            type: 'hidden',
            value: data?.value ?? '',
            dataType: data?.dataType ?? 'text',
            label: data?.label ?? '',
            crmFieldData: data?.crmFieldData ?? null
        };
        const fieldHtml = this.renderer.render(fieldData);
        this.htmlElement.append(fieldHtml);
        this.elementsMap.set(fieldHtml.id, fieldHtml);
        return fieldHtml;
    }
    removeElement(elementId) {
        const element = this.elementsMap.get(elementId);
        if (!element) {
            console.warn('HiddenFieldBlock removeElement: element not found', elementId);
            return;
        }
        element.remove();
        this.elementsMap.delete(elementId);
    }
    initElements() {
        this.elementsMap.clear();
        const elementsNodeList = this.htmlElement.querySelectorAll(SelectorUtil.builderElement.base);
        for (let i = 0; i < elementsNodeList.length; i++) {
            const elementNode = elementsNodeList.item(i);
            if (!elementNode.id) {
                console.warn('HiddenFieldBlock element', elementNode, 'not have id');
                continue;
            }
            if (elementNode.hasAttribute(SelectorUtil.elementAttr)) {
                const elementType = elementNode.getAttribute(SelectorUtil.elementAttr);
                if (validHiddenTypes.includes(elementType)) {
                    this.elementsMap.set(elementNode.id, elementNode);
                }
                else {
                    console.warn(`HiddenField with type "${elementType}" not found`);
                }
            }
        }
    }
    toJsonData() {
        return this.elements.map(element => {
            const jsonAttr = element.getAttribute('data-element-json');
            if (jsonAttr) {
                return JSON.parse(jsonAttr);
            }
            console.warn('Hidden field missing data-element-json:', element.id);
            return {
                id: element.id,
                type: 'hidden',
                value: '',
                dataType: 'text',
                label: '',
                crmFieldData: null
            };
        });
    }
}

function getAddressFieldsConfig() {
    return [
        {
            fieldType: 'text',
            createOptions: {
                initialData: {
                    id: '',
                    type: 'text',
                    label: TranslateUtils.ADDRESS_1_FIELD.LABEL,
                    placeholder: TranslateUtils.ADDRESS_1_FIELD.PLACEHOLDER,
                    showPlaceholder: true,
                    required: true,
                    crmFieldData: {
                        mixedType: 'ContactField',
                        type: 'Text',
                        name: 'Address Line 1',
                        systemName: 'AddressLine1'
                    }
                }
            }
        },
        {
            fieldType: 'text',
            createOptions: {
                initialData: {
                    id: '',
                    type: 'text',
                    label: TranslateUtils.ADDRESS_2_FIELD.LABEL,
                    crmFieldData: {
                        mixedType: 'ContactField',
                        type: 'Text',
                        name: 'Address Line 2',
                        systemName: 'AddressLine2'
                    }
                }
            }
        },
        {
            fieldType: 'text',
            createOptions: {
                initialData: {
                    id: '',
                    type: 'text',
                    label: TranslateUtils.CITY_FIELD.LABEL,
                    required: true,
                    crmFieldData: {
                        mixedType: 'ContactField',
                        type: 'Text',
                        name: 'City',
                        systemName: 'AddressCity'
                    }
                }
            }
        },
        {
            fieldType: 'text',
            createOptions: {
                initialData: {
                    id: '',
                    type: 'text',
                    label: TranslateUtils.STATE_FIELD.LABEL,
                    required: true,
                    crmFieldData: {
                        mixedType: 'ContactField',
                        type: 'Text',
                        name: 'State/Province/Region',
                        systemName: 'AddressState'
                    }
                }
            }
        },
        {
            fieldType: 'text',
            createOptions: {
                initialData: {
                    id: '',
                    type: 'text',
                    label: TranslateUtils.POSTAL_FIELD.LABEL,
                    required: true,
                    crmFieldData: {
                        mixedType: 'ContactField',
                        type: 'Text',
                        name: 'ZIP/Postal Code',
                        systemName: 'AddressZip'
                    }
                }
            }
        },
        {
            fieldType: 'dropdown',
            createOptions: {
                initialData: {
                    id: '',
                    type: 'dropdown',
                    label: TranslateUtils.COUNTRY_FIELD.LABEL,
                    required: true,
                    options: Object.values(TranslateUtils.COUNTRY).map(country => ({
                        value: country,
                        label: country
                    })),
                    crmFieldData: {
                        mixedType: 'ContactField',
                        type: 'Text',
                        name: 'Country',
                        systemName: 'AddressCountry'
                    }
                }
            }
        },
    ];
}

class LinkData {
    static get noneData() {
        return {
            type: 'none',
            value: ''
        };
    }
    static composeLinkData(link, target) {
        if (!link || link === '#') {
            return this.noneData;
        }
        if (link.startsWith('mailto:', 0)) {
            return {
                type: 'email',
                value: link
            };
        }
        if (link.startsWith('tel:', 0)) {
            return {
                type: 'phone',
                value: link
            };
        }
        if (link.startsWith('http', 0) || link.startsWith('https', 0)) {
            return {
                type: 'url',
                value: link,
                target: target ?? '_blank'
            };
        }
        return this.noneData;
    }
    static getTargetValueByLink(link) {
        if (link.type === 'url') {
            return link.target;
        }
        return '_self';
    }
}

function formStylesDataToCssVariables(styles) {
    const vars = [];
    for (const [key, value] of Object.entries(styles)) {
        if (value != null && value !== '') {
            const cssVar = '--_tfb-' + key.replace(/([A-Z])/g, '-$1').toLowerCase();
            vars.push(`    ${cssVar}: ${value};`);
        }
    }
    return vars.length > 0 ? `:host {\n${vars.join('\n')}\n}` : '';
}

class FormStylesDataService {
    constructor() {
        this._styles = {};
        this._styleElement = null;
    }
    get styles() {
        return this._styles;
    }
    get(prop) {
        return this._styles[prop];
    }
    set(prop, value) {
        if (value === null || value === '') {
            const { [prop]: removedValue, ...rest } = this._styles;
            this._styles = rest;
        }
        else {
            this._styles = { ...this._styles, [prop]: value };
        }
    }
    load(data) {
        this._styles = data ? { ...data } : {};
    }
    /** Bootstrap-only styles adoption for page mounts: seeds the shared live map
     *  ONCE — on the first mount after boot (the stack's
     *  sharedStylesDataService starts empty). Later mounts KEEP the live map: form
     *  styles are form-scoped, and a blind load() used to clobber the frame-wide var
     *  block with a stale per-page snapshot (the sidebar flushes live, every page save
     *  re-stamps the live styles into that page's JSON). Explicit reloads (the styles
     *  panel's reset, a fresh stack boot) keep using load(). */
    seed(data) {
        if (Object.keys(this._styles).length === 0) {
            this._styles = data ? { ...data } : {};
        }
    }
    applyToDocument(doc) {
        const cssBlock = formStylesDataToCssVariables(this._styles);
        const textContent = cssBlock
            ? cssBlock.replace(':host', ':root')
            : '';
        // One doc-wide element, id-addressed and REUSED — form styles are form-scoped,
        // not page-scoped, so the whole frame shares one element; an identical rewrite
        // is skipped outright (no style recalc on remount)
        let styleElement = doc.getElementById('tfb-styles-data');
        if (!styleElement) {
            styleElement = doc.createElement('style');
            styleElement.id = 'tfb-styles-data';
            doc.head.appendChild(styleElement);
        }
        this._styleElement = styleElement;
        if (styleElement.textContent !== textContent) {
            styleElement.textContent = textContent;
        }
    }
    detachFromDocument() {
        this._styleElement?.remove();
        this._styleElement = null;
    }
}

class Guid {
    static { this.Empty = '00000000000000000000000000000000'; }
    static newGuid() {
        let res = '';
        const alphabet = '0123456789abcedf';
        for (let i = 0; i < 32; i++) {
            res += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
        }
        return res;
    }
    static toShortGuid(guid) {
        return guid?.split('-').join('') ?? '';
    }
    static isEmptyGuid(value) {
        if (value.replace(/-/g, '') === Guid.Empty) {
            return true;
        }
        return false;
    }
    static isGuid(value) {
        return this.isShortGuid(value) || this.isLongGuid(value);
    }
    static isShortGuid(value) {
        return new RegExp(/[a-f0-9]{32}/, 'i').test(value);
    }
    static isLongGuid(value) {
        return new RegExp(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/, 'i').test(value);
    }
}

class BaseCrmFieldData {
    constructor(data) {
        this.mixedType = data.mixedType;
        this.type = data.type;
        this.name = data.name;
    }
}
class CustomFieldData extends BaseCrmFieldData {
    constructor(data) {
        super(data);
        this.mixedType = 'CustomField';
        this.id = data.id;
    }
    get key() {
        return Guid.toShortGuid(this.id);
    }
}
class ContactFieldData extends BaseCrmFieldData {
    constructor(data) {
        super(data);
        this.mixedType = 'ContactField';
        this.systemName = data.systemName;
    }
    get key() {
        return this.systemName;
    }
}

class TextFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'text',
            label: TranslateUtils.TEXT_FIELD.LABEL,
            placeholder: TranslateUtils.TEXT_FIELD.PLACEHOLDER,
            description: '',
            required: false,
            hiddenLabel: false
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'text';
        input.maxLength = 500;
        this.applyCommonAttributes(input, fieldJsonData);
        return input;
    }
}

class EmailFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'email',
            label: TranslateUtils.EMAIL_FIELD.LABEL,
            placeholder: TranslateUtils.EMAIL_FIELD.PLACEHOLDER,
            required: false,
            hiddenLabel: false,
            crmFieldData: {
                mixedType: 'ContactField',
                type: 'Email',
                systemName: 'Email',
                name: 'Email'
            }
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'email';
        input.maxLength = 254;
        this.applyCommonAttributes(input, fieldJsonData);
        return input;
    }
}

class DropdownFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        const option = TranslateUtils.DROPDOWN_FIELD.OPTION;
        return {
            id: '',
            type: 'dropdown',
            label: TranslateUtils.DROPDOWN_FIELD.LABEL,
            placeholder: TranslateUtils.DROPDOWN_FIELD.PLACEHOLDER,
            required: false,
            hiddenLabel: false,
            options: [
                { value: `${option} 1`, label: `${option} 1` },
                { value: `${option} 2`, label: `${option} 2` },
                { value: `${option} 3`, label: `${option} 3` }
            ]
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(fieldJsonData, options = {}) {
        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');
        const fieldContainer = document.createElement('div');
        fieldContainer.classList.add('field');
        if (fieldJsonData.required) {
            fieldContainer.classList.add('required');
        }
        if (!fieldJsonData.hiddenLabel) {
            const label = this.renderLabel(fieldJsonData);
            fieldContainer.appendChild(label);
        }
        const dropdown = document.createElement('div');
        dropdown.classList.add('dropdown');
        dropdown.setAttribute('tabindex', '0');
        const input = document.createElement('input');
        input.id = `field-${fieldJsonData.id}`;
        input.classList.add('dropdown-input', 'input', 'default');
        input.type = 'text';
        input.readOnly = true;
        input.setAttribute('tabindex', '-1');
        input.name = fieldJsonData.id;
        if (fieldJsonData.showPlaceholder && fieldJsonData.placeholder) {
            input.placeholder = fieldJsonData.placeholder || '';
        }
        if (fieldJsonData.required) {
            input.required = true;
        }
        const plainLabel = this.getPlainLabel(fieldJsonData.label);
        if (plainLabel) {
            input.dataset['label'] = plainLabel;
        }
        dropdown.appendChild(input);
        const dropdownIcon = document.createElement('div');
        dropdownIcon.classList.add('dropdown-icon');
        const arrowButton = document.createElement('button');
        arrowButton.classList.add('dropdown-arrow-icon');
        arrowButton.type = 'button';
        arrowButton.setAttribute('tabindex', '-1');
        const iconSpan = document.createElement('span');
        iconSpan.classList.add('icon', 'icon-arrow-down');
        arrowButton.appendChild(iconSpan);
        dropdownIcon.appendChild(arrowButton);
        dropdown.appendChild(dropdownIcon);
        const optionsContainer = document.createElement('div');
        optionsContainer.classList.add('options');
        const fieldOptions = fieldJsonData.options || [];
        fieldOptions.forEach(option => {
            const optionElement = this.createOption(option, fieldJsonData.id);
            optionsContainer.appendChild(optionElement);
        });
        dropdown.appendChild(optionsContainer);
        fieldContainer.appendChild(dropdown);
        const errorContainer = this.renderErrorContainer(fieldJsonData);
        fieldContainer.appendChild(errorContainer);
        if (fieldJsonData.showDescription) {
            const description = this.renderDescription(fieldJsonData);
            fieldContainer.appendChild(description);
        }
        elementContent.appendChild(fieldContainer);
        return elementContent;
    }
    createOption(option, parentId) {
        const optionElement = document.createElement('div');
        optionElement.classList.add('option');
        optionElement.id = DomUtils.generateUniqueElementId([]);
        optionElement.setAttribute('name', `dropdown_${parentId}`);
        optionElement.setAttribute('tabindex', '0');
        optionElement.innerHTML = HtmlUtils.sanitize(option.label);
        return optionElement;
    }
    renderInput() {
        return document.createElement('div');
    }
}

class TextareaFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'textarea',
            label: TranslateUtils.TEXTAREA_FIELD.LABEL,
            placeholder: TranslateUtils.TEXTAREA_FIELD.PLACEHOLDER,
            description: '',
            required: false,
            hiddenLabel: false
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const textarea = document.createElement('textarea');
        textarea.classList.add('textarea', 'input');
        textarea.maxLength = 65535;
        this.applyCommonAttributes(textarea, fieldJsonData);
        if (fieldJsonData.height) {
            textarea.style.height = `${fieldJsonData.height}px`;
        }
        return textarea;
    }
}

class NumberFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'number',
            label: TranslateUtils.NUMBER_FIELD.LABEL,
            placeholder: TranslateUtils.NUMBER_FIELD.PLACEHOLDER,
            required: false,
            hiddenLabel: false
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'text';
        this.applyCommonAttributes(input, fieldJsonData);
        return input;
    }
}

class TelFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'tel',
            label: TranslateUtils.TEL_FIELD.LABEL,
            placeholder: TranslateUtils.TEL_FIELD.PLACEHOLDER,
            required: false,
            hiddenLabel: false,
            crmFieldData: {
                mixedType: 'ContactField',
                type: 'Phone',
                systemName: 'MobilePhone',
                name: 'Mobile Phone'
            }
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'tel';
        this.applyCommonAttributes(input, fieldJsonData);
        return input;
    }
}

class UrlFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'url',
            label: TranslateUtils.URL_FIELD.LABEL,
            placeholder: TranslateUtils.URL_FIELD.PLACEHOLDER,
            description: '',
            required: false,
            hiddenLabel: false
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'text';
        input.maxLength = 2048;
        this.applyCommonAttributes(input, fieldJsonData);
        return input;
    }
}

class DateFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'date',
            label: TranslateUtils.DATE_FIELD.LABEL,
            description: '',
            format: TranslateUtils.DATE_FIELD.FORMAT,
            fillCurrentDate: false,
            required: false,
            hiddenLabel: false
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'text';
        input.maxLength = 11;
        input.dataset['format'] = fieldJsonData.format || TranslateUtils.DATE_FIELD.FORMAT;
        input.dataset['fillCurrentDate'] = String(fieldJsonData.fillCurrentDate ?? false);
        this.applyCommonAttributes(input, fieldJsonData);
        return input;
    }
}

class CheckboxFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        const choice = TranslateUtils.CHECKBOX_FIELD.CHOICE;
        return {
            id: '',
            type: 'checkbox',
            label: TranslateUtils.CHECKBOX_FIELD.LABEL,
            required: false,
            hiddenLabel: false,
            options: [
                { value: `${choice} 1`, label: `${choice} 1` },
                { value: `${choice} 2`, label: `${choice} 2` },
                { value: `${choice} 3`, label: `${choice} 3` }
            ]
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(fieldJsonData, options = {}) {
        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');
        const fieldset = document.createElement('fieldset');
        fieldset.classList.add('field', 'checkbox');
        if (fieldJsonData.required) {
            fieldset.classList.add('required');
        }
        fieldset.setAttribute('min-count', fieldJsonData.minCount ?? '');
        fieldset.setAttribute('max-count', fieldJsonData.maxCount ?? '');
        if (!fieldJsonData.hiddenLabel) {
            const legend = document.createElement('legend');
            legend.classList.add('label');
            this.applyHtmlLabel(legend, fieldJsonData.label);
            fieldset.appendChild(legend);
        }
        const optionsContainer = document.createElement('div');
        optionsContainer.classList.add('options');
        const fieldOptions = fieldJsonData.options || [];
        const fieldLabel = this.getPlainLabel(fieldJsonData.label);
        fieldOptions.forEach((option, index) => {
            const optionElement = this.createOption(option, fieldJsonData.id, index, fieldLabel);
            optionsContainer.appendChild(optionElement);
        });
        fieldset.appendChild(optionsContainer);
        const errorContainer = this.renderErrorContainer(fieldJsonData);
        fieldset.appendChild(errorContainer);
        if (fieldJsonData.showDescription) {
            const description = this.renderDescription(fieldJsonData);
            fieldset.appendChild(description);
        }
        elementContent.appendChild(fieldset);
        return elementContent;
    }
    createOption(option, parentId, index, fieldLabel) {
        const optionElement = document.createElement('div');
        optionElement.classList.add('option');
        const inputId = `${parentId}_opt_${index}`;
        const input = document.createElement('input');
        input.id = inputId;
        input.type = 'checkbox';
        input.classList.add('input');
        input.name = parentId;
        input.value = HtmlUtils.escapeHtml(option.value);
        input.dataset['label'] = fieldLabel;
        const label = document.createElement('label');
        label.classList.add('label');
        label.setAttribute('for', inputId);
        label.innerHTML = HtmlUtils.sanitize(option.label);
        optionElement.append(input, label);
        return optionElement;
    }
    renderInput() {
        return document.createElement('div');
    }
}

class RadioFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        const option = TranslateUtils.RADIO_FIELD.OPTION;
        return {
            id: '',
            type: 'radio',
            label: TranslateUtils.RADIO_FIELD.LABEL,
            required: false,
            hiddenLabel: false,
            options: [
                { value: `${option} 1`, label: `${option} 1` },
                { value: `${option} 2`, label: `${option} 2` },
                { value: `${option} 3`, label: `${option} 3` }
            ]
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(fieldJsonData, options = {}) {
        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');
        const fieldset = document.createElement('fieldset');
        fieldset.classList.add('field', 'field__radio');
        if (fieldJsonData.required) {
            fieldset.classList.add('required');
        }
        if (!fieldJsonData.hiddenLabel) {
            const legend = document.createElement('legend');
            legend.classList.add('label');
            this.applyHtmlLabel(legend, fieldJsonData.label);
            fieldset.appendChild(legend);
        }
        const optionsContainer = document.createElement('div');
        optionsContainer.classList.add('options');
        const fieldOptions = fieldJsonData.options || [];
        const fieldLabel = this.getPlainLabel(fieldJsonData.label);
        fieldOptions.forEach((option, index) => {
            const optionElement = this.createOption(option, fieldJsonData.id, index, fieldLabel);
            optionsContainer.appendChild(optionElement);
        });
        fieldset.appendChild(optionsContainer);
        const errorContainer = this.renderErrorContainer(fieldJsonData);
        fieldset.appendChild(errorContainer);
        if (fieldJsonData.showDescription) {
            const description = this.renderDescription(fieldJsonData);
            fieldset.appendChild(description);
        }
        elementContent.appendChild(fieldset);
        return elementContent;
    }
    createOption(option, parentId, index, fieldLabel) {
        const optionElement = document.createElement('div');
        optionElement.classList.add('option');
        const inputId = `${parentId}_opt_${index}`;
        const input = document.createElement('input');
        input.id = inputId;
        input.type = 'radio';
        input.classList.add('input');
        input.name = parentId;
        input.value = HtmlUtils.escapeHtml(option.value);
        input.dataset['label'] = fieldLabel;
        const label = document.createElement('label');
        label.classList.add('label');
        label.setAttribute('for', inputId);
        label.innerHTML = HtmlUtils.sanitize(option.label);
        optionElement.append(input, label);
        return optionElement;
    }
    renderInput() {
        return document.createElement('div');
    }
}

class FileFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'file',
            label: TranslateUtils.FILE_FIELD,
            required: false,
            hiddenLabel: false,
            fileSelectText: TranslateUtils.UPLOAD_FILE
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(fieldJsonData, options = {}) {
        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');
        const fieldContainer = document.createElement('div');
        fieldContainer.classList.add('field');
        fieldContainer.classList.toggle('required', fieldJsonData.required);
        if (!fieldJsonData.hiddenLabel) {
            const label = this.renderLabel(fieldJsonData);
            fieldContainer.appendChild(label);
        }
        const fileSelectLabel = document.createElement('label');
        fileSelectLabel.classList.add('file-select-label');
        fileSelectLabel.classList.toggle('required', fieldJsonData.required);
        fileSelectLabel.setAttribute('tabindex', '0');
        const input = document.createElement('input');
        input.classList.add('input');
        input.id = `field-${fieldJsonData.id}`;
        input.type = 'file';
        input.name = fieldJsonData.id;
        if (fieldJsonData.required) {
            input.required = true;
        }
        const plainLabel = this.getPlainLabel(fieldJsonData.label);
        if (plainLabel) {
            input.dataset['label'] = plainLabel;
        }
        fileSelectLabel.appendChild(input);
        const fileSelectText = document.createElement('span');
        fileSelectText.classList.add('file-select-text');
        fileSelectText.textContent = fieldJsonData.fileSelectText || TranslateUtils.UPLOAD_FILE;
        fileSelectLabel.appendChild(fileSelectText);
        fieldContainer.appendChild(fileSelectLabel);
        const errorContainer = this.renderErrorContainer(fieldJsonData);
        fieldContainer.appendChild(errorContainer);
        if (fieldJsonData.showDescription) {
            const description = this.renderDescription(fieldJsonData);
            fieldContainer.appendChild(description);
        }
        elementContent.appendChild(fieldContainer);
        return elementContent;
    }
    renderInput() {
        return document.createElement('div');
    }
}

class PasswordFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'password',
            label: TranslateUtils.PASSWORD_FIELD.LABEL,
            placeholder: TranslateUtils.PASSWORD_FIELD.PLACEHOLDER,
            description: '',
            required: false,
            hiddenLabel: false,
            crmFieldData: null,
            showPasswordToggle: false,
            showPasswordStrength: false,
            confirmation: {
                enabled: false,
                label: TranslateUtils.PASSWORD_FIELD.CONFIRMATION_LABEL,
                placeholder: TranslateUtils.PASSWORD_FIELD.CONFIRMATION_PLACEHOLDER,
                description: '',
                hiddenLabel: false
            }
        };
    }
    renderContent(fieldJsonData, options = {}) {
        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');
        // Main password field
        const fieldContainer = document.createElement('div');
        fieldContainer.classList.add('field');
        fieldContainer.classList.toggle('required', fieldJsonData.required === true);
        if (!fieldJsonData.hiddenLabel) {
            const label = this.renderLabel(fieldJsonData);
            fieldContainer.appendChild(label);
        }
        const input = this.renderInput(fieldJsonData, options);
        const inputWrapper = this.wrapInputWithToggle(input, fieldJsonData.showPasswordToggle);
        fieldContainer.appendChild(inputWrapper);
        const errorContainer = this.renderErrorContainer(fieldJsonData);
        fieldContainer.appendChild(errorContainer);
        if (fieldJsonData.showDescription) {
            const description = this.renderDescription(fieldJsonData);
            fieldContainer.appendChild(description);
        }
        elementContent.appendChild(fieldContainer);
        if (fieldJsonData.showPasswordStrength) {
            const isRuntime = !!options.runtimeContext;
            const strengthRules = this.renderPasswordStrengthRules(fieldJsonData.id, isRuntime);
            elementContent.appendChild(strengthRules);
        }
        if (fieldJsonData.confirmation?.enabled) {
            const confirmationContainer = this.renderConfirmationField(fieldJsonData);
            elementContent.appendChild(confirmationContainer);
        }
        return elementContent;
    }
    renderConfirmationField(fieldJsonData) {
        const confirmation = fieldJsonData.confirmation;
        const fieldContainer = document.createElement('div');
        fieldContainer.classList.add('field', 'password-confirmation');
        fieldContainer.classList.toggle('required', fieldJsonData.required === true);
        if (!confirmation.hiddenLabel) {
            const label = document.createElement('label');
            label.classList.add('label');
            label.setAttribute('for', `field-${fieldJsonData.id}-confirm`);
            this.applyHtmlLabel(label, confirmation.label || TranslateUtils.PASSWORD_FIELD.CONFIRMATION_LABEL);
            fieldContainer.appendChild(label);
        }
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'password';
        input.maxLength = 500;
        input.id = `field-${fieldJsonData.id}-confirm`;
        input.name = `${fieldJsonData.id}-confirm`;
        input.dataset['confirmFor'] = fieldJsonData.id;
        if (fieldJsonData.required) {
            input.required = true;
        }
        if (confirmation.placeholder) {
            input.placeholder = confirmation.placeholder;
        }
        const plainLabel = this.getPlainLabel(confirmation.label);
        if (plainLabel) {
            input.dataset['label'] = plainLabel;
        }
        input.setAttribute('aria-required', fieldJsonData.required ? 'true' : 'false');
        input.setAttribute('aria-invalid', 'false');
        const inputWrapper = this.wrapInputWithToggle(input, fieldJsonData.showPasswordToggle);
        fieldContainer.appendChild(inputWrapper);
        if (confirmation.description) {
            const description = document.createElement('span');
            description.classList.add('description');
            description.id = `description-${fieldJsonData.id}-confirm`;
            description.textContent = confirmation.description;
            input.setAttribute('aria-describedby', `description-${fieldJsonData.id}-confirm`);
            fieldContainer.appendChild(description);
        }
        const errorContainer = document.createElement('div');
        errorContainer.classList.add('fieldJsonData-errors');
        errorContainer.id = `errors-${fieldJsonData.id}-confirm`;
        errorContainer.setAttribute('role', 'alert');
        errorContainer.setAttribute('aria-live', 'assertive');
        fieldContainer.appendChild(errorContainer);
        return fieldContainer;
    }
    renderPasswordStrengthRules(fieldId, isRuntime = false) {
        const container = document.createElement('div');
        container.classList.add('password-strength-rules');
        if (isRuntime) {
            container.classList.add('untouched');
        }
        container.dataset['strengthFor'] = fieldId;
        const rules = [
            { key: 'length', text: TranslateUtils.PASSWORD_FIELD.STRENGTH.MIN_LENGTH },
            { key: 'case', text: TranslateUtils.PASSWORD_FIELD.STRENGTH.MIXED_CASE },
            { key: 'number', text: TranslateUtils.PASSWORD_FIELD.STRENGTH.HAS_NUMBER }
        ];
        rules.forEach(rule => {
            const ruleElement = document.createElement('div');
            ruleElement.classList.add('password-strength-rule');
            ruleElement.dataset['rule'] = rule.key;
            const icon = document.createElement('span');
            icon.classList.add('rule-icon');
            const text = document.createElement('span');
            text.classList.add('rule-text');
            text.textContent = rule.text;
            ruleElement.append(icon, text);
            container.appendChild(ruleElement);
        });
        return container;
    }
    wrapInputWithToggle(input, showToggle) {
        if (!showToggle) {
            return input;
        }
        const wrapper = document.createElement('div');
        wrapper.classList.add('password-input-wrapper');
        wrapper.appendChild(input);
        const toggleButton = this.renderPasswordToggle();
        wrapper.appendChild(toggleButton);
        return wrapper;
    }
    renderPasswordToggle() {
        const toggleButton = document.createElement('button');
        toggleButton.type = 'button';
        toggleButton.classList.add('password-toggle');
        toggleButton.setAttribute('aria-label', TranslateUtils.PASSWORD_FIELD.SHOW_PASSWORD);
        toggleButton.setAttribute('tabindex', '-1');
        const iconShow = document.createElement('span');
        iconShow.classList.add('icon', 'icon-eye');
        toggleButton.appendChild(iconShow);
        const iconHide = document.createElement('span');
        iconHide.classList.add('icon', 'icon-eye-off');
        toggleButton.appendChild(iconHide);
        toggleButton.addEventListener('click', () => {
            const wrapper = toggleButton.closest('.password-input-wrapper');
            if (!wrapper)
                return;
            const input = wrapper.querySelector('.input');
            if (!input)
                return;
            const isPasswordVisible = input.type === 'text';
            if (isPasswordVisible) {
                input.type = 'password';
                toggleButton.classList.remove('active');
                toggleButton.setAttribute('aria-label', TranslateUtils.PASSWORD_FIELD.SHOW_PASSWORD);
            }
            else {
                input.type = 'text';
                toggleButton.classList.add('active');
                toggleButton.setAttribute('aria-label', TranslateUtils.PASSWORD_FIELD.HIDE_PASSWORD);
            }
        });
        return toggleButton;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'password';
        input.maxLength = 500;
        this.applyCommonAttributes(input, fieldJsonData);
        return input;
    }
}

class FirstNameFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'firstname',
            label: TranslateUtils.FIRSTNAME_FIELD.LABEL,
            placeholder: TranslateUtils.FIRSTNAME_FIELD.PLACEHOLDER,
            required: true,
            hiddenLabel: false,
            crmFieldData: {
                mixedType: 'ContactField',
                type: 'Text',
                systemName: 'FirstName',
                name: 'First Name'
            }
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'text';
        input.maxLength = 500;
        this.applyCommonAttributes(input, fieldJsonData);
        return input;
    }
}

class LastNameFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'lastname',
            label: TranslateUtils.LASTNAME_FIELD.LABEL,
            placeholder: TranslateUtils.LASTNAME_FIELD.PLACEHOLDER,
            required: true,
            hiddenLabel: false,
            crmFieldData: {
                mixedType: 'ContactField',
                type: 'Text',
                systemName: 'LastName',
                name: 'Last Name'
            }
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'text';
        input.maxLength = 500;
        this.applyCommonAttributes(input, fieldJsonData);
        return input;
    }
}

class CompanyNameFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'companyname',
            label: TranslateUtils.COMPANYNAME_FIELD.LABEL,
            placeholder: TranslateUtils.COMPANYNAME_FIELD.PLACEHOLDER,
            required: false,
            hiddenLabel: false,
            crmFieldData: {
                mixedType: 'ContactField',
                type: 'Text',
                systemName: 'Company',
                name: 'Company'
            }
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'text';
        input.maxLength = 500;
        this.applyCommonAttributes(input, fieldJsonData);
        return input;
    }
}

class BirthdayFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'birthday',
            label: TranslateUtils.BIRTHDAY_FIELD.LABEL,
            format: TranslateUtils.BIRTHDAY_FIELD.FORMAT,
            placeholder: TranslateUtils.BIRTHDAY_FIELD.PLACEHOLDER,
            required: false,
            hiddenLabel: false,
            crmFieldData: {
                mixedType: 'ContactField',
                type: 'DateTime',
                systemName: 'Birthday',
                name: 'Birthdate'
            }
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input', 'default');
        input.type = 'text';
        input.maxLength = 11;
        input.dataset['format'] = fieldJsonData.format || TranslateUtils.BIRTHDAY_FIELD.FORMAT;
        this.applyCommonAttributes(input, fieldJsonData);
        return input;
    }
}

class SignatureFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'signature',
            label: TranslateUtils.SIGNATURE_FIELD.LABEL,
            placeholder: TranslateUtils.SIGNATURE_FIELD.PLACEHOLDER,
            clearButton: TranslateUtils.SIGNATURE_FIELD.CLEAR_BUTTON,
            required: true,
            hiddenLabel: false
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(fieldJsonData, options = {}) {
        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');
        const fieldContainer = document.createElement('div');
        fieldContainer.classList.add('field');
        fieldContainer.classList.toggle('required', fieldJsonData.required);
        if (!fieldJsonData.hiddenLabel) {
            const label = this.renderLabel(fieldJsonData);
            fieldContainer.appendChild(label);
        }
        const canvasContainer = document.createElement('div');
        canvasContainer.classList.add('signature-placeholder');
        canvasContainer.classList.toggle('required', fieldJsonData.required);
        if (fieldJsonData.height) {
            canvasContainer.style.height = `${fieldJsonData.height}px`;
        }
        const canvas = document.createElement('canvas');
        canvas.classList.add('canvas');
        canvas.id = `field-${fieldJsonData.id}-canvas`;
        canvas.setAttribute('aria-label', fieldJsonData.placeholder || TranslateUtils.SIGNATURE_FIELD.PLACEHOLDER);
        const overlay = document.createElement('div');
        overlay.classList.add('overlay');
        const overlayText = document.createElement('span');
        overlayText.classList.add('overlay-text');
        overlayText.textContent = fieldJsonData.placeholder || TranslateUtils.SIGNATURE_FIELD.PLACEHOLDER;
        const overlaySignature = document.createElement('span');
        overlaySignature.classList.add('overlay-signature');
        overlay.append(overlayText, overlaySignature);
        canvasContainer.append(canvas, overlay);
        const errorContainer = this.renderErrorContainer(fieldJsonData);
        const clearLink = document.createElement('a');
        clearLink.classList.add('signature-clear');
        clearLink.textContent = fieldJsonData.clearButton || TranslateUtils.SIGNATURE_FIELD.CLEAR_BUTTON;
        clearLink.href = 'javascript:void(0)';
        const input = this.renderInput(fieldJsonData, options);
        fieldContainer.append(canvasContainer, errorContainer, clearLink, input);
        if (fieldJsonData.showDescription) {
            const description = this.renderDescription(fieldJsonData);
            fieldContainer.appendChild(description);
        }
        elementContent.appendChild(fieldContainer);
        return elementContent;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input');
        input.id = `field-${fieldJsonData.id}`;
        input.type = 'file';
        input.name = fieldJsonData.id;
        input.accept = 'image/png';
        input.hidden = true;
        input.tabIndex = -1;
        input.setAttribute('aria-hidden', 'true');
        if (fieldJsonData.required) {
            input.required = true;
        }
        const plainLabel = this.getPlainLabel(fieldJsonData.label);
        if (plainLabel) {
            input.dataset['label'] = plainLabel;
        }
        return input;
    }
}

class SubscribeFieldRenderer extends BaseFieldRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'subscribe',
            label: TranslateUtils.SUBSCRIBE_FIELD.LABEL,
            required: false,
            hiddenLabel: false,
            crmFieldData: {
                mixedType: 'ContactField',
                type: 'Boolean',
                systemName: 'Subscribe',
                name: 'Subscribe'
            }
        };
    }
    renderContent(fieldJsonData, options = {}) {
        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');
        const fieldContainer = document.createElement('div');
        fieldContainer.classList.add('field', 'checkbox');
        fieldContainer.classList.toggle('required', fieldJsonData.required === true);
        const optionsContainer = document.createElement('div');
        optionsContainer.classList.add('options');
        const inputContainer = document.createElement('div');
        inputContainer.classList.add('input-container', 'option');
        const input = this.renderInput(fieldJsonData, options);
        inputContainer.appendChild(input);
        if (!fieldJsonData.hiddenLabel) {
            const label = this.renderLabel(fieldJsonData);
            inputContainer.appendChild(label);
        }
        optionsContainer.appendChild(inputContainer);
        fieldContainer.appendChild(optionsContainer);
        const errorContainer = this.renderErrorContainer(fieldJsonData);
        fieldContainer.appendChild(errorContainer);
        if (fieldJsonData.showDescription) {
            const description = this.renderDescription(fieldJsonData);
            fieldContainer.appendChild(description);
        }
        elementContent.appendChild(fieldContainer);
        return elementContent;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderInput(fieldJsonData, options) {
        const input = document.createElement('input');
        input.classList.add('input');
        input.type = 'checkbox';
        input.value = 'true';
        input.checked = fieldJsonData.defaultValue ?? false;
        this.applyCommonAttributes(input, fieldJsonData);
        return input;
    }
}

class FieldRendererFactory {
    constructor() {
        this.renderers = new Map([
            ['text', new TextFieldRenderer()],
            ['email', new EmailFieldRenderer()],
            ['firstname', new FirstNameFieldRenderer()],
            ['lastname', new LastNameFieldRenderer()],
            ['companyname', new CompanyNameFieldRenderer()],
            ['birthday', new BirthdayFieldRenderer()],
            ['signature', new SignatureFieldRenderer()],
            ['subscribe', new SubscribeFieldRenderer()],
            ['textarea', new TextareaFieldRenderer()],
            ['tel', new TelFieldRenderer()],
            ['url', new UrlFieldRenderer()],
            ['number', new NumberFieldRenderer()],
            ['date', new DateFieldRenderer()],
            ['dropdown', new DropdownFieldRenderer()],
            ['checkbox', new CheckboxFieldRenderer()],
            ['radio', new RadioFieldRenderer()],
            ['file', new FileFieldRenderer()],
            ['hidden', new HiddenFieldRenderer()],
            ['password', new PasswordFieldRenderer()]
        ]);
    }
    getRenderer(fieldType) {
        const renderer = this.renderers.get(fieldType.toLowerCase());
        if (!renderer) {
            throw new Error(`No renderer registered for field type: ${fieldType}`);
        }
        return renderer;
    }
}

class BaseWidgetRenderer extends RendererBase {
    renderElement(widgetJsonData, options = {}) {
        const wrapper = document.createElement('div');
        wrapper.id = widgetJsonData.id;
        const content = this.renderContent(widgetJsonData, options);
        wrapper.appendChild(content);
        wrapper.dataset['element'] = 'widget';
        wrapper.dataset['widget'] = widgetJsonData.type.toLowerCase();
        this.applyAdditionalAttributes(wrapper, widgetJsonData);
        this.applyCustomCssClasses(wrapper, widgetJsonData.cssClasses, options);
        return wrapper;
    }
    rerender(element, widgetJsonData, options = {}) {
        const oldContent = element.querySelector('.element-content');
        if (oldContent) {
            oldContent.remove();
        }
        const newContent = this.renderContent(widgetJsonData, options);
        element.appendChild(newContent);
        this.applyAdditionalAttributes(element, widgetJsonData);
        this.updateData(element, widgetJsonData);
    }
    applyVisualProperties(element, data) {
        this.applyAdditionalAttributes(element, data);
    }
    applyCustomCssClasses(element, cssClasses, options = {}) {
        const isEditor = options.runtimeContext?.mode?.toLocaleLowerCase() === 'editor';
        if (isEditor) {
            return; // we don't add custom classes in the constructor because it may affect the constructor elements.
        }
        if (cssClasses && cssClasses.length > 0) {
            element.classList.add(...cssClasses);
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    applyAdditionalAttributes(element, widgetJsonData) {
    }
}

/**
 * The managed-button row's DOM contract — the shared half of the submit and
 * nav-button renderers: the .button-element builder (variant class + sanitized
 * label span) and the wrapper's button-group attributes. The class names and the
 * 'buttons' group marker are the runtime's navigation hooks (Web.Static's Form
 * binds .button-element--previous/--next) and the server's submit-row scaffold
 * mirrors them — one place on each side, pinned by WidgetSchemaParityTest.
 */
class BaseButtonWidgetRenderer extends BaseWidgetRenderer {
    /** The default row height of a managed button — server parity
     *  (FormPageContentService.DefaultButtonHeight). */
    static { this.defaultButtonHeight = '40px'; }
    /** One managed button: .button-element--{variant} + a sanitized label span. */
    createButton(variant, label, type = 'button') {
        const button = document.createElement('button');
        button.classList.add('button-element', `button-element--${variant}`);
        button.type = type;
        const labelSpan = document.createElement('span');
        labelSpan.classList.add('button-label');
        labelSpan.innerHTML = HtmlUtils.sanitize(label);
        button.appendChild(labelSpan);
        return button;
    }
    /** The wrapper attributes shared by every managed-button widget: the 'buttons'
     *  element group and the row height. */
    applyButtonElementAttributes(element, height) {
        element.dataset['group'] = 'buttons';
        element.style.height = height;
    }
}

function normalizeNavDirection(value) {
    if (typeof value === 'number') {
        return value === 1 ? 'previous' : 'next';
    }
    return typeof value === 'string' && value.toLowerCase() === 'previous' ? 'previous' : 'next';
}
function normalizeSubmitVariant(value) {
    if (typeof value === 'number') {
        return value === 1 ? 'extended' : 'simple';
    }
    return typeof value === 'string' && value.toLowerCase() === 'extended' ? 'extended' : 'simple';
}

class SubmitWidgetRenderer extends BaseButtonWidgetRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'submit',
            variant: 'simple',
            submitLabel: TranslateUtils.SUBMIT_WIDGET.LABEL,
            previousLabel: TranslateUtils.SUBMIT_WIDGET.PREVIOUS_LABEL,
            nextLabel: TranslateUtils.SUBMIT_WIDGET.NEXT_LABEL,
            height: SubmitWidgetRenderer.defaultButtonHeight
        };
    }
    createNew(options) {
        const defaultData = super.createDefaultData();
        if (options?.isExtendedVariant) {
            defaultData.variant = 'extended';
        }
        return this.render(defaultData, options);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(widgetJsonData, options) {
        const content = document.createElement('div');
        content.classList.add('element-content');
        const isExtended = normalizeSubmitVariant(widgetJsonData.variant) === 'extended';
        if (isExtended) {
            if (widgetJsonData.previousLabel !== null && widgetJsonData.previousLabel !== undefined) {
                const prevButton = this.createButton('previous', widgetJsonData.previousLabel || TranslateUtils.SUBMIT_WIDGET.PREVIOUS_LABEL);
                content.appendChild(prevButton);
            }
            if (widgetJsonData.nextLabel !== null && widgetJsonData.nextLabel !== undefined) {
                const nextButton = this.createButton('next', widgetJsonData.nextLabel || TranslateUtils.SUBMIT_WIDGET.NEXT_LABEL);
                content.appendChild(nextButton);
            }
            if (widgetJsonData.submitLabel !== null && widgetJsonData.submitLabel !== undefined) {
                const submitButton = this.createButton('submit', widgetJsonData.submitLabel || TranslateUtils.SUBMIT_WIDGET.LABEL, 'submit');
                content.appendChild(submitButton);
            }
        }
        else {
            // null submitLabel = "hidden by page position" (serve-time prepare nulls the label
            // on first/middle pages of nav-button format forms) — render no button, same
            // null-guards as the extended branch above. Legacy snapshots are unaffected:
            // their Simple serves always carry a non-null label (defaultData is non-null too,
            // so the editor canvas keeps rendering the button).
            if (widgetJsonData.submitLabel !== null && widgetJsonData.submitLabel !== undefined) {
                const submitButton = this.createButton('submit', widgetJsonData.submitLabel || TranslateUtils.SUBMIT_WIDGET.LABEL, 'submit');
                content.appendChild(submitButton);
            }
        }
        return content;
    }
    applyAdditionalAttributes(element, widgetJsonData) {
        super.applyAdditionalAttributes(element, widgetJsonData);
        if (normalizeSubmitVariant(widgetJsonData.variant) === 'extended') {
            element.dataset['variant'] = 'extended';
        }
        this.applyButtonElementAttributes(element, widgetJsonData.height);
    }
}

class TextWidgetRenderer extends BaseWidgetRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'text',
            content: ''
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(widgetJsonData, options) {
        const content = document.createElement('div');
        content.classList.add('element-content');
        const textElement = document.createElement('div');
        textElement.dataset['inlineInject'] = '';
        textElement.classList.add('ck-content');
        if (widgetJsonData.content) {
            textElement.innerHTML = widgetJsonData.content;
        }
        content.appendChild(textElement);
        return content;
    }
}

class HeaderWidgetRenderer extends BaseWidgetRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'header',
            content: ''
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(widgetJsonData, options) {
        const content = document.createElement('div');
        content.classList.add('element-content');
        const textElement = document.createElement('div');
        textElement.dataset['inlineInject'] = '';
        textElement.dataset['widget'] = 'header';
        textElement.classList.add('ck-content');
        if (widgetJsonData.content) {
            textElement.innerHTML = widgetJsonData.content;
        }
        content.appendChild(textElement);
        return content;
    }
}

class DividerWidgetRenderer extends BaseWidgetRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'divider',
            thickness: 1,
            color: '#000000'
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(widgetJsonData, options) {
        const content = document.createElement('div');
        content.classList.add('element-content');
        const divider = document.createElement('hr');
        divider.classList.add('divider-element');
        if (widgetJsonData.thickness) {
            divider.style.borderWidth = `${widgetJsonData.thickness}px`;
        }
        if (widgetJsonData.color) {
            divider.style.borderColor = widgetJsonData.color;
        }
        content.appendChild(divider);
        return content;
    }
}

class ImageWidgetRenderer extends BaseWidgetRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'image',
            src: '',
            alt: '',
            style: { height: '200px', heightFixed: false },
        };
    }
    renderContent(widgetJsonData, options) {
        const content = document.createElement('div');
        content.classList.add('element-content');
        if (widgetJsonData.src) {
            this.removePlaceholder(content);
            const resolvedSrc = this.resolveUrl(widgetJsonData.src, options.baseUrl);
            this.renderImage(content, { ...widgetJsonData, src: resolvedSrc });
        }
        else {
            this.removeImage(content);
            this.renderPlaceholder(content);
        }
        return content;
    }
    resolveUrl(src, baseUrl) {
        if (baseUrl && src.startsWith('/')) {
            return `${baseUrl}${src}`;
        }
        return src;
    }
    renderPlaceholder(content) {
        let notAvailableElement = content.querySelector('.not-available');
        if (!notAvailableElement) {
            notAvailableElement = document.createElement('div');
            notAvailableElement.classList.add('not-available');
            content.appendChild(notAvailableElement);
        }
    }
    removePlaceholder(content) {
        const notAvailableElement = content.querySelector('.not-available');
        notAvailableElement?.remove();
    }
    renderImage(element, data) {
        let img = element.querySelector('img');
        if (!img) {
            img = document.createElement('img');
            img.classList.add('image');
        }
        if (img.getAttribute('src') !== data.src) {
            img.src = data.src || '';
        }
        img.alt = data.alt || '';
        this.applyImageStyles(img, element, data);
        element.appendChild(img);
    }
    applyImageStyles(img, container, data) {
        const style = data.style;
        const isContainMode = (style?.fitMode ?? 'cover') === 'contain';
        img.style.objectFit = isContainMode ? 'contain' : 'cover';
        img.style.objectPosition = '';
        this.applyImageSize(img, container, isContainMode);
        img.onload = () => this.applyImageSize(img, container, isContainMode);
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = this.toJustifyContent(style?.imageAlignment);
        const r = style?.borderRadius;
        img.style.borderRadius = r
            ? `${r.topLeftPx ?? 0}px ${r.topRightPx ?? 0}px ${r.bottomRightPx ?? 0}px ${r.bottomLeftPx ?? 0}px`
            : '';
        const thickness = style?.borderThickness ?? 0;
        if (thickness > 0) {
            img.style.borderWidth = `${thickness}px`;
            img.style.borderStyle = 'solid';
            img.style.borderColor = style?.borderColor ?? '';
        }
        else {
            img.style.borderWidth = '';
            img.style.borderStyle = '';
            img.style.borderColor = '';
        }
    }
    applyImageSize(img, container, isContainMode) {
        if (!isContainMode) {
            img.style.width = '100%';
            img.style.height = '100%';
            return;
        }
        const mainAspect = this.calculateMainAspect(container, img);
        switch (mainAspect) {
            case ImageMainAspect.Width:
                img.style.width = 'auto';
                img.style.height = '100%';
                break;
            case ImageMainAspect.Height:
                img.style.width = '100%';
                img.style.height = 'auto';
                break;
            default:
                img.style.width = '100%';
                img.style.height = '100%';
        }
    }
    toJustifyContent(alignment) {
        switch (alignment) {
            case 'start': {
                return 'flex-start';
            }
            case 'end': {
                return 'flex-end';
            }
            default: {
                return 'center';
            }
        }
    }
    calculateMainAspect(container, img) {
        if (container.offsetWidth === 0 || container.offsetHeight === 0 ||
            img.naturalWidth === 0 || img.naturalHeight === 0) {
            return ImageMainAspect.None;
        }
        const containerRatio = container.offsetWidth / container.offsetHeight;
        const imageRatio = img.naturalWidth / img.naturalHeight;
        return containerRatio >= imageRatio ? ImageMainAspect.Width : ImageMainAspect.Height;
    }
    removeImage(element) {
        const img = element.querySelector('img');
        img?.remove();
    }
    applyAdditionalAttributes(element, widgetJsonData) {
        this.renderHeightStyles(element, widgetJsonData.style);
    }
    renderHeightStyles(element, style) {
        const content = element.querySelector('.element-content');
        if (style?.heightFixed !== false) {
            element.style.height = style?.height ?? '';
            element.style.aspectRatio = '';
            element.style.position = '';
            element.classList.remove('aspect-ratio-resizable');
            if (content) {
                content.style.position = '';
                content.style.top = '';
                content.style.left = '';
                content.style.bottom = '';
                content.style.right = '';
            }
        }
        else {
            const aspectRatio = style.aspectRatio ?? 16 / 9;
            element.style.aspectRatio = String(aspectRatio);
            element.style.height = '';
            element.style.position = 'relative';
            element.classList.add('aspect-ratio-resizable');
            if (content) {
                content.style.position = 'absolute';
                content.style.top = '0';
                content.style.left = '0';
                content.style.bottom = '0';
                content.style.right = '0';
            }
        }
    }
    applyVisualProperties(element, data) {
        this.rerender(element, data);
    }
}
var ImageMainAspect;
(function (ImageMainAspect) {
    ImageMainAspect[ImageMainAspect["Width"] = 0] = "Width";
    ImageMainAspect[ImageMainAspect["Height"] = 1] = "Height";
    ImageMainAspect[ImageMainAspect["None"] = 2] = "None";
})(ImageMainAspect || (ImageMainAspect = {}));

class SpacerWidgetRenderer extends BaseWidgetRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'spacer',
            height: '20px',
            shouldUseMaxHeight: true,
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(widgetJsonData, options) {
        const content = document.createElement('div');
        content.classList.add('element-content');
        return content;
    }
    applyAdditionalAttributes(element, widgetJsonData) {
        element.style.height = widgetJsonData.shouldUseMaxHeight ? '100%' : widgetJsonData.height;
    }
}

class SignInWidgetRenderer extends BaseWidgetRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'signin',
            content: TranslateUtils.SIGN_IN_WIDGET.TEXT
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(widgetJsonData, options) {
        const content = document.createElement('div');
        content.classList.add('element-content');
        const textElement = document.createElement('div');
        textElement.dataset['inlineInject'] = '';
        textElement.classList.add('ck-content');
        if (widgetJsonData.content) {
            textElement.innerHTML = widgetJsonData.content;
        }
        content.appendChild(textElement);
        return content;
    }
}

class SignUpWidgetRenderer extends BaseWidgetRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'signup',
            content: TranslateUtils.SIGN_UP_WIDGET.TEXT
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(widgetJsonData, options) {
        const content = document.createElement('div');
        content.classList.add('element-content');
        const textElement = document.createElement('div');
        textElement.dataset['inlineInject'] = '';
        textElement.classList.add('ck-content');
        if (widgetJsonData.content) {
            textElement.innerHTML = widgetJsonData.content;
        }
        content.appendChild(textElement);
        return content;
    }
}

class ForgotPasswordWidgetRenderer extends BaseWidgetRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'forgotpassword',
            content: TranslateUtils.FORGOT_PASSWORD_WIDGET.TEXT
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(widgetJsonData, options) {
        const content = document.createElement('div');
        content.classList.add('element-content');
        const textElement = document.createElement('div');
        textElement.dataset['inlineInject'] = '';
        textElement.classList.add('ck-content');
        if (widgetJsonData.content) {
            textElement.innerHTML = widgetJsonData.content;
        }
        content.appendChild(textElement);
        return content;
    }
}

class NavButtonWidgetRenderer extends BaseButtonWidgetRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'navbutton',
            direction: 'next',
            label: TranslateUtils.SUBMIT_WIDGET.NEXT_LABEL,
            height: NavButtonWidgetRenderer.defaultButtonHeight
        };
    }
    createNew(options) {
        const defaultData = super.createDefaultData();
        if (options?.direction) {
            const direction = normalizeNavDirection(options.direction);
            defaultData.direction = direction;
            defaultData.label = this.getDefaultLabel(direction);
        }
        return this.render(defaultData, options);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderContent(widgetJsonData, options) {
        const content = document.createElement('div');
        content.classList.add('element-content');
        const direction = this.normalizeDirection(widgetJsonData.direction);
        content.appendChild(this.createButton(direction, widgetJsonData.label || this.getDefaultLabel(direction)));
        return content;
    }
    applyAdditionalAttributes(element, widgetJsonData) {
        super.applyAdditionalAttributes(element, widgetJsonData);
        const direction = this.normalizeDirection(widgetJsonData.direction);
        element.dataset['buttonRole'] = direction;
        this.applyButtonElementAttributes(element, widgetJsonData.height);
    }
    // The wire format is lowercase strings on every client path (Web.Api Shared
    // models, Web.Host presenter); the shared normalizer additionally tolerates the
    // numeric Domain representation and odd casing — see nav-direction.utils.ts
    normalizeDirection(direction) {
        return normalizeNavDirection(direction);
    }
    getDefaultLabel(direction) {
        return direction === 'previous'
            ? TranslateUtils.SUBMIT_WIDGET.PREVIOUS_LABEL
            : TranslateUtils.SUBMIT_WIDGET.NEXT_LABEL;
    }
}

class WidgetRendererFactory {
    constructor() {
        this.renderers = new Map([
            ['submit', new SubmitWidgetRenderer()],
            ['text', new TextWidgetRenderer()],
            ['header', new HeaderWidgetRenderer()],
            ['divider', new DividerWidgetRenderer()],
            ['image', new ImageWidgetRenderer()],
            ['spacer', new SpacerWidgetRenderer()],
            ['signin', new SignInWidgetRenderer()],
            ['signup', new SignUpWidgetRenderer()],
            ['forgotpassword', new ForgotPasswordWidgetRenderer()],
            ['navbutton', new NavButtonWidgetRenderer()]
        ]);
    }
    getRenderer(widgetType) {
        const renderer = this.renderers.get(widgetType.toLowerCase());
        if (!renderer) {
            throw new Error(`No renderer registered for widget type: ${widgetType}`);
        }
        return renderer;
    }
}

class BaseSpecialRenderer {
    render(data, options) {
        const element = this.renderElement(data, options);
        element.dataset['elementJson'] = JSON.stringify(data);
        return element;
    }
    getData(element) {
        const jsonData = element.dataset['elementJson'];
        if (!jsonData) {
            return null;
        }
        try {
            return JSON.parse(jsonData);
        }
        catch (e) {
            console.error('Failed to parse element data:', e);
            return null;
        }
    }
    createNew(options) {
        const defaultData = this.createDefaultData();
        return this.render(defaultData, options);
    }
    createDefaultData() {
        const data = this.defaultData;
        data.id = this.generateId();
        return data;
    }
    generateId() {
        return DomUtils.generateUniqueElementId([]);
    }
    /**
     * Create error container for validation messages
     */
    createErrorContainer() {
        const container = document.createElement('div');
        container.classList.add('field-error-container');
        container.setAttribute('role', 'alert');
        container.setAttribute('aria-live', 'polite');
        return container;
    }
}

class CaptchaRenderer extends BaseSpecialRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'captcha'
        };
    }
    renderElement(data, options) {
        const captchaConfig = options?.runtimeContext?.captcha;
        if (!captchaConfig) {
            console.warn('Captcha runtime config not provided');
            const wrapper = document.createElement('div');
            wrapper.classList.add('field-wrapper', 'field-captcha', 'captcha-not-configured');
            return wrapper;
        }
        switch (captchaConfig.type) {
            case 'TruCaptcha':
                return this.renderTruCaptcha(data, captchaConfig);
            case 'ReCaptchaV2':
                return this.renderReCaptchaV2(data, captchaConfig);
            case 'ReCaptchaV3':
                return this.renderReCaptchaV3(data, captchaConfig);
            default:
                console.warn(`Unknown Captcha type: ${captchaConfig.type}`);
                return document.createElement('div');
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    renderTruCaptcha(data, config) {
        const container = document.createElement('div');
        container.classList.add('field-wrapper', 'field-captcha', 'tru-captcha-container');
        container.dataset['element'] = 'widget';
        container.dataset['widget'] = 'captcha';
        container.setAttribute('data-captcha-type', 'truCaptcha');
        const captchaInputId = `captcha-${this.generateId()}`;
        const labelWrapper = document.createElement('div');
        labelWrapper.classList.add('tru-captcha-label-wrapper');
        const label = document.createElement('label');
        label.htmlFor = captchaInputId;
        label.classList.add('tru-captcha-label');
        label.textContent = 'Enter text from image';
        const asterisk = document.createElement('span');
        asterisk.classList.add('tru-captcha-required-marker');
        asterisk.textContent = '*';
        label.appendChild(asterisk);
        labelWrapper.appendChild(label);
        const imageRowWrapper = document.createElement('div');
        imageRowWrapper.classList.add('tru-captcha-image-row-wrapper');
        const imageWrapper = document.createElement('div');
        imageWrapper.classList.add('tru-captcha-image-wrapper');
        const image = document.createElement('img');
        image.classList.add('tru-captcha-image');
        image.alt = 'Image with captcha';
        image.src = '';
        imageWrapper.appendChild(image);
        const refreshWrapper = document.createElement('div');
        refreshWrapper.classList.add('tru-captcha-refresh-wrapper');
        const refreshButton = document.createElement('button');
        refreshButton.classList.add('tru-captcha-refresh-icon');
        refreshButton.type = 'button';
        refreshWrapper.appendChild(refreshButton);
        imageRowWrapper.appendChild(imageWrapper);
        imageRowWrapper.appendChild(refreshWrapper);
        const inputWrapper = document.createElement('div');
        inputWrapper.classList.add('tru-captcha-input-wrapper', 'field');
        const input = document.createElement('input');
        input.type = 'text';
        input.id = captchaInputId;
        input.classList.add('tru-captcha-control', 'input', 'default');
        input.name = '__truCaptchaText';
        input.required = true;
        inputWrapper.appendChild(input);
        container.appendChild(labelWrapper);
        container.appendChild(imageRowWrapper);
        container.appendChild(inputWrapper);
        this.initializeTruCaptcha(container, image, refreshButton);
        return container;
    }
    renderReCaptchaV2(data, config) {
        const container = document.createElement('div');
        container.classList.add('field-wrapper', 'field-captcha', 'recaptcha-v2-container');
        container.dataset['element'] = 'widget';
        container.dataset['widget'] = 'captcha';
        container.setAttribute('data-captcha-type', 'googleReCaptcha');
        const captchaId = this.generateId().replace(/-/g, '');
        const wrapperId = `recaptcha-${captchaId}`;
        const wrapper = document.createElement('div');
        wrapper.classList.add('recaptcha-wrapper');
        wrapper.id = wrapperId;
        container.appendChild(wrapper);
        const siteKey = config.siteKey || '';
        const theme = config.darkMode ? 'dark' : 'light';
        const showBadge = config.showBadge || false;
        container.setAttribute('data-recaptcha-site-key', siteKey);
        container.setAttribute('data-recaptcha-theme', theme);
        if (showBadge) {
            container.setAttribute('data-recaptcha-invisible', 'true');
        }
        const windowKey = `__recaptchaElement_${captchaId}`;
        window[windowKey] = wrapper;
        const callbackScript = document.createElement('script');
        callbackScript.type = 'text/javascript';
        callbackScript.classList.add('recaptcha-callback');
        callbackScript.textContent = `
            function sendRecaptchaCallback_${captchaId}() {
                const event = new CustomEvent('recaptcha-callback', { bubbles: true });
                const captchaWidget = window.__recaptchaElement_${captchaId};
                captchaWidget?.dispatchEvent(event);
            }

            var onloadCallback_${captchaId} = function() {
                if (typeof grecaptcha === 'undefined') return;
                var element = window.__recaptchaElement_${captchaId};
                if (!element) return;
                grecaptcha.render(element, {
                    'sitekey': '${siteKey}',
                    'theme': '${theme}'${showBadge ? `,
                    'callback': () => sendRecaptchaCallback_${captchaId}(),
                    'size': 'invisible',
                    'badge': 'inline'` : ''}
                });
            };

            if (typeof grecaptcha !== 'undefined' && grecaptcha.render) {
                onloadCallback_${captchaId}();
            } else {
                window.addEventListener('load', () => {
                    if (typeof grecaptcha !== 'undefined' && grecaptcha.render) {
                        onloadCallback_${captchaId}();
                    }
                });
            }
        `;
        container.appendChild(callbackScript);
        this.loadGoogleReCaptchaScript();
        return container;
    }
    renderReCaptchaV3(data, config) {
        const container = document.createElement('div');
        container.classList.add('field-wrapper', 'field-captcha', 'recaptcha-v3-container');
        container.dataset['element'] = 'widget';
        container.dataset['widget'] = 'captcha';
        container.setAttribute('data-captcha-type', 'googleReCaptchaV3');
        const siteKey = config.siteKey || '';
        container.setAttribute('data-recaptcha-site-key', siteKey);
        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');
        container.appendChild(elementContent);
        this.loadGoogleReCaptchaV3Script(siteKey);
        return container;
    }
    initializeTruCaptcha(container, image, refreshButton) {
        // Detect base URL from script src or use current origin
        const baseUrl = this.detectBaseUrl();
        const loadCaptcha = async () => {
            try {
                // POST to create new captcha challenge
                const response = await fetch(`${baseUrl}/api/public/tru-captcha`, {
                    method: 'POST'
                });
                const captchaId = await response.json();
                // Set image URL
                image.src = `${baseUrl}/api/public/tru-captcha/image/${captchaId}`;
                let captchaIdInput = container.querySelector('input[name="__truCaptchaId"]');
                if (!captchaIdInput) {
                    captchaIdInput = document.createElement('input');
                    captchaIdInput.type = 'hidden';
                    captchaIdInput.name = '__truCaptchaId';
                    container.appendChild(captchaIdInput);
                }
                captchaIdInput.value = captchaId;
            }
            catch (error) {
                console.error('Failed to load TruCaptcha:', error);
            }
        };
        container.truCaptchaRefresh = loadCaptcha;
        void loadCaptcha();
        refreshButton.addEventListener('click', async () => {
            await loadCaptcha();
        });
    }
    detectBaseUrl() {
        // Try to detect API URL from tru-form element's api-url attribute
        const truForm = document.querySelector('tru-form[api-url]');
        if (truForm) {
            const apiUrl = truForm.getAttribute('api-url');
            if (apiUrl)
                return apiUrl;
        }
        // Try to detect from script src
        const scripts = Array.from(document.querySelectorAll('script[src*="tru-forms-embed"]'));
        for (let i = 0; i < scripts.length; i++) {
            const src = scripts[i].getAttribute('src');
            if (src) {
                try {
                    const url = new URL(src, window.location.href);
                    return `${url.protocol}//${url.host}`;
                }
                catch {
                    // Ignore invalid URLs
                }
            }
        }
        // Fallback to current origin
        return window.location.origin;
    }
    loadGoogleReCaptchaScript() {
        if (document.querySelector('script.recaptcha-js-resource')) {
            return;
        }
        const script = document.createElement('script');
        script.classList.add('recaptcha-js-resource');
        script.src = 'https://www.google.com/recaptcha/api.js?render=explicit&hl=en';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
    }
    loadGoogleReCaptchaV3Script(siteKey) {
        if (document.querySelector('script.recaptcha-v3-js-resource')) {
            return;
        }
        const script = document.createElement('script');
        script.classList.add('recaptcha-v3-js-resource');
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}&hl=en`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
    }
}

class GdprRenderer extends BaseSpecialRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'gdpr'
        };
    }
    renderElement(data, options) {
        const gdprConfig = options?.runtimeContext?.gdpr;
        if (!gdprConfig || !gdprConfig.enabled) {
            return document.createElement('div');
        }
        const wrapper = document.createElement('div');
        wrapper.classList.add('field-wrapper', 'field-gdpr');
        const label = document.createElement('label');
        label.classList.add('gdpr-label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = `field-${data.id}`;
        input.name = data.id || '__gdprConsent';
        input.classList.add('field-input', 'gdpr-checkbox');
        input.value = 'true';
        input.required = true;
        input.setAttribute('aria-required', 'true');
        input.setAttribute('aria-invalid', 'false');
        const consentTextSpan = document.createElement('span');
        consentTextSpan.classList.add('gdpr-consent-text');
        if (gdprConfig.privacyPolicyUrl) {
            const textParts = gdprConfig.consentText.split(/(\[privacy policy\]|\[Privacy Policy\])/i);
            textParts.forEach(part => {
                if (/\[privacy policy\]/i.test(part)) {
                    const link = document.createElement('a');
                    link.href = gdprConfig.privacyPolicyUrl;
                    link.textContent = 'Privacy Policy';
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    consentTextSpan.appendChild(link);
                }
                else {
                    consentTextSpan.appendChild(document.createTextNode(part));
                }
            });
        }
        else {
            consentTextSpan.textContent = gdprConfig.consentText;
        }
        label.appendChild(input);
        label.appendChild(consentTextSpan);
        wrapper.appendChild(label);
        const errorContainer = this.createErrorContainer();
        wrapper.appendChild(errorContainer);
        return wrapper;
    }
}

class AccountIdRenderer extends BaseSpecialRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'accountId'
        };
    }
    renderElement(data, options) {
        const accountConfig = options?.runtimeContext?.account;
        const container = document.createElement('div');
        container.classList.add('account-id-container');
        container.style.display = 'none';
        if (!accountConfig?.accountId) {
            console.warn('Account runtime config not provided');
            return container;
        }
        const input = document.createElement('input');
        input.type = 'hidden';
        input.id = '__accountId';
        input.name = '__accountId';
        input.value = accountConfig.accountId;
        container.appendChild(input);
        return container;
    }
}

class SpecialRendererFactory {
    constructor() {
        this.renderers = new Map([
            ['captcha', new CaptchaRenderer()],
            ['gdpr', new GdprRenderer()],
            ['accountId', new AccountIdRenderer()],
        ]);
    }
    getRenderer(type) {
        const renderer = this.renderers.get(type);
        if (!renderer) {
            throw new Error(`No renderer registered for special field type: ${type}`);
        }
        return renderer;
    }
}

class DomQuery {
    static getRow(id, root = document) {
        return root.querySelector(`#${CSS.escape(id)}`);
    }
    static getColumn(id, root = document) {
        return root.querySelector(`#${CSS.escape(id)}`);
    }
    static getSections(container) {
        return Array.from(container.querySelectorAll(SelectorUtil.section.base));
    }
    static getMeshes(section) {
        return Array.from(section.querySelectorAll(SelectorUtil.mesh.base));
    }
    static getMeshContainer(mesh) {
        return mesh.querySelector(`[${SelectorUtil.meshContainerAttr}]`);
    }
    static getRows(mesh) {
        return Array.from(mesh.querySelectorAll(SelectorUtil.row.base));
    }
    static getColumns(row) {
        return Array.from(row.querySelectorAll(SelectorUtil.column.base));
    }
    static getElements(column) {
        return Array.from(column.querySelectorAll(SelectorUtil.builderElement.base));
    }
    static getParentColumn(element) {
        return element.closest(SelectorUtil.column.base);
    }
    static getParentRow(element) {
        return element.closest(SelectorUtil.row.base);
    }
    static getParentMesh(element) {
        return element.closest(SelectorUtil.mesh.base);
    }
    static getParentSection(element) {
        return element.closest(SelectorUtil.section.base);
    }
    static isColumnEmpty(column) {
        const elements = DomQuery.getElements(column);
        if (elements.length === 0) {
            return true;
        }
        return elements.every(el => el.style.display === 'none');
    }
    static getColumnSize(column) {
        const colSize = column.dataset['colSize'];
        return colSize ? parseInt(colSize, 10) : 12;
    }
    static isRowEmpty(row) {
        const columns = DomQuery.getColumns(row);
        if (columns.length === 0) {
            return true;
        }
        return columns.every(col => DomQuery.isColumnEmpty(col));
    }
    static isMeshEmpty(mesh) {
        const rows = DomQuery.getRows(mesh);
        if (rows.length === 0) {
            return true;
        }
        return rows.every(row => DomQuery.isRowEmpty(row));
    }
    static getSectionType(section) {
        return section.getAttribute(SelectorUtil.sectionAttr);
    }
    static isSectionEmpty(section) {
        const meshes = DomQuery.getMeshes(section);
        if (meshes.length === 0) {
            return true;
        }
        return meshes.every(mesh => DomQuery.isMeshEmpty(mesh));
    }
    static isHiddenElement(element) {
        return element.getAttribute(SelectorUtil.elementAttr) === 'hidden';
    }
    static updateFirstLastColumnClasses(row) {
        const columns = DomQuery.getColumns(row);
        columns.forEach(col => col.classList.remove('first-col', 'last-col'));
        if (columns.length > 0) {
            columns[0].classList.add('first-col');
            columns[columns.length - 1].classList.add('last-col');
        }
    }
    static getHierarchy(id, root = document) {
        const startElement = root.querySelector(`#${CSS.escape(id)}`);
        if (!startElement) {
            return {};
        }
        return DomQuery.getHierarchyFromElement(startElement);
    }
    static getHierarchyFromElement(element) {
        const result = {};
        const isBuilderElement = element.hasAttribute(SelectorUtil.elementAttr);
        const isColumn = element.hasAttribute(SelectorUtil.columnAttr);
        const isRow = element.hasAttribute(SelectorUtil.rowAttr);
        const isMesh = element.hasAttribute(SelectorUtil.meshAttr);
        const isSection = element.hasAttribute(SelectorUtil.sectionAttr);
        if (isBuilderElement && DomQuery.isHiddenElement(element)) {
            result.element = element;
            return result;
        }
        if (isBuilderElement) {
            result.element = element;
            result.column = DomQuery.getParentColumn(element) ?? undefined;
            result.row = result.column ? DomQuery.getParentRow(result.column) ?? undefined : undefined;
            result.mesh = result.row ? DomQuery.getParentMesh(result.row) ?? undefined : undefined;
            result.section = result.mesh ? DomQuery.getParentSection(result.mesh) ?? undefined : undefined;
        }
        else if (isColumn) {
            result.column = element;
            result.row = DomQuery.getParentRow(element) ?? undefined;
            result.mesh = result.row ? DomQuery.getParentMesh(result.row) ?? undefined : undefined;
            result.section = result.mesh ? DomQuery.getParentSection(result.mesh) ?? undefined : undefined;
        }
        else if (isRow) {
            result.row = element;
            result.mesh = DomQuery.getParentMesh(element) ?? undefined;
            result.section = result.mesh ? DomQuery.getParentSection(result.mesh) ?? undefined : undefined;
        }
        else if (isMesh) {
            result.mesh = element;
            result.section = DomQuery.getParentSection(element) ?? undefined;
        }
        else if (isSection) {
            result.section = element;
        }
        return result;
    }
}

class ElementUtils {
    static getElementType(element) {
        const type = element.getAttribute(SelectorUtil.elementAttr);
        if (type === 'field' || type === 'widget' || type === 'hidden') {
            return type;
        }
        return null;
    }
    static isField(element) {
        return ElementUtils.getElementType(element) === 'field';
    }
    static isWidget(element) {
        return ElementUtils.getElementType(element) === 'widget';
    }
    static isHidden(element) {
        return ElementUtils.getElementType(element) === 'hidden';
    }
    static getFieldType(element) {
        if (!ElementUtils.isField(element)) {
            return null;
        }
        return element.getAttribute(SelectorUtil.fieldAttr);
    }
    static getWidgetType(element) {
        if (!ElementUtils.isWidget(element)) {
            return null;
        }
        return element.getAttribute(SelectorUtil.widgetAttr);
    }
    static getWidgetData(element) {
        if (!ElementUtils.isWidget(element)) {
            return null;
        }
        const jsonData = element.dataset['elementJson'];
        if (!jsonData) {
            return null;
        }
        try {
            const data = JSON.parse(jsonData);
            data.type = data.type.toLowerCase();
            return data;
        }
        catch (e) {
            console.error('[toJSON] Failed to parse element JSON:', e);
            return null;
        }
    }
    static isSelected(element) {
        return element.classList.contains('selected');
    }
    static setSelected(element, selected) {
        if (selected === ElementUtils.isSelected(element)) {
            return;
        }
        element.classList.toggle('selected', selected);
        if (selected) {
            element.focus();
            ElementUtils.dispatchEvent(element, 'fb.element.select');
        }
        else {
            element.blur();
            ElementUtils.dispatchEvent(element, 'fb.element.unselect');
        }
    }
    static select(element) {
        ElementUtils.setSelected(element, true);
    }
    static unselect(element) {
        ElementUtils.setSelected(element, false);
    }
    static getData(element) {
        const jsonData = element.dataset['elementJson'];
        if (!jsonData)
            return null;
        try {
            return JSON.parse(jsonData);
        }
        catch {
            return null;
        }
    }
    static setData(element, data) {
        element.dataset['elementJson'] = JSON.stringify(data);
    }
    static updateData(element, updates) {
        const currentData = ElementUtils.getData(element) || {};
        Object.assign(currentData, updates);
        ElementUtils.setData(element, currentData);
    }
    static remove(element) {
        element.remove();
    }
    static dispatchEvent(element, eventType, additionalInfo) {
        const event = new CustomEvent(eventType, {
            bubbles: true,
            detail: {
                id: element.id,
                // The dispatcher's page travels with the payload: element ids duplicate
                // across pages after a split/merge (the server copies them verbatim),
                // so consumers cannot scope by id alone
                pageId: element.closest(SelectorUtil.page.base)?.dataset['pageId'],
                additionalInfo
            }
        });
        element.ownerDocument.dispatchEvent(event);
    }
}

class BaseGridRenderer {
    render(data, options) {
        return this.renderElement(data, options);
    }
    createNew(options) {
        const data = { ...this.defaultData };
        data.id = DomUtils.generateUniqueElementId(options?.idsBlackList ?? []);
        return this.render(data, options);
    }
}

class SectionRenderer extends BaseGridRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'section',
            sectionType: 'form'
        };
    }
    createNew(options) {
        const data = { ...this.defaultData };
        data.id = DomUtils.generateUniqueElementId([]);
        if (options?.type) {
            data.sectionType = options.type;
        }
        return this.render(data, options);
    }
    renderElement(sectionJsonData) {
        const element = document.createElement('section');
        element.id = sectionJsonData.id;
        element.dataset['section'] = sectionJsonData.sectionType.toLowerCase();
        return element;
    }
}

class MeshRenderer extends BaseGridRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'mesh'
        };
    }
    renderElement(meshJsonData) {
        const element = document.createElement('div');
        element.id = meshJsonData.id;
        element.dataset['mesh'] = '';
        const container = document.createElement('div');
        container.dataset['meshContainer'] = '';
        container.classList.add('mesh-container');
        element.appendChild(container);
        return element;
    }
}

class ColumnRenderer extends BaseGridRenderer {
    get defaultData() {
        return {
            id: '',
            type: 'column',
            size: 12
        };
    }
    createNew(options) {
        const data = { ...this.defaultData };
        data.id = DomUtils.generateUniqueElementId(options?.idsBlackList ?? []);
        if (options?.size !== undefined) {
            data.size = options.size;
        }
        return this.render(data, options);
    }
    renderElement(columnJsonData, options) {
        const colSize = this.getEnsuredColSize(columnJsonData.size);
        const element = document.createElement('div');
        element.id = columnJsonData.id;
        element.dataset['colSize'] = `${colSize}`;
        element.dataset['meshCol'] = '';
        element.classList.add('col', `col-${colSize}`);
        element.classList.toggle('empty', options?.empty ?? true);
        return element;
    }
    getEnsuredColSize(size) {
        return Math.min(size, 12);
    }
}

class RowRenderer extends BaseGridRenderer {
    constructor() {
        super(...arguments);
        this.maxColumns = 12;
        this.columnRenderer = new ColumnRenderer();
    }
    get defaultData() {
        return {
            id: '',
            type: 'row'
        };
    }
    createNew(options) {
        const element = super.createNew(options);
        if (!options || options.renderColumns !== false) {
            const idsBlackList = [element.id];
            for (let i = 0; i < this.maxColumns; i++) {
                const column = this.columnRenderer.createNew({
                    size: 1,
                    empty: true,
                    idsBlackList
                });
                element.append(column);
                idsBlackList.push(column.id);
            }
        }
        return element;
    }
    renderElement(rowJsonData) {
        const element = document.createElement('div');
        element.id = rowJsonData.id;
        element.classList.add('mesh-row', 'empty');
        element.dataset['meshRow'] = '';
        return element;
    }
}

class HiddenBlockRenderer {
    render(pageId) {
        const container = document.createElement('div');
        container.id = pageId
            ? SelectorUtil.hiddenVarsBlockIdFor(pageId)
            : SelectorUtil.hiddenVarsBlockId;
        container.setAttribute(SelectorUtil.hiddenVarsBlockAttr, '');
        container.style.display = 'none';
        return container;
    }
}

/**
 * Utility for replacing {{key}} placeholders with values from a variables map
 */
/**
 * Replaces all occurrences of {{key}} with corresponding values from the variables map
 * @param text - The text containing placeholders
 * @param variables - Key-value pairs for replacement
 * @returns The text with all placeholders replaced
 */
function replaceVariables(text, variables) {
    if (!text) {
        return '';
    }
    // Match all {{key}} patterns
    const pattern = /\{\{([^}]+)\}\}/g;
    return text.replace(pattern, (match, key) => {
        const trimmedKey = key.trim();
        if (Object.prototype.hasOwnProperty.call(variables, trimmedKey)) {
            const value = variables[trimmedKey];
            return value !== undefined && value !== null ? String(value) : match;
        }
        return '';
    });
}
/**
 * Deep clone an object with variable replacement in string values
 */
function cloneWithVariableReplacement(obj, variables) {
    if (obj === null || obj === undefined) {
        return obj;
    }
    if (typeof obj === 'string') {
        return replaceVariables(obj, variables);
    }
    if (Array.isArray(obj)) {
        return obj.map(item => cloneWithVariableReplacement(item, variables));
    }
    if (typeof obj === 'object') {
        const result = {};
        for (const key of Object.keys(obj)) {
            result[key] = cloneWithVariableReplacement(obj[key], variables);
        }
        return result;
    }
    return obj;
}
/**
 * Apply variable replacement to form page data
 * This creates a deep clone with all string values having {{key}} replaced
 */
function applyVariablesToPageData(data, variables) {
    if (!variables || Object.keys(variables).length === 0) {
        return data;
    }
    const result = cloneWithVariableReplacement(data, variables);
    removeEmptyWidgets(result);
    return result;
}
function removeEmptyWidgets(page) {
    page.sections?.forEach((section) => {
        section.meshes?.forEach((mesh) => {
            mesh.rows?.forEach((row) => {
                row.columns?.forEach((column) => {
                    column.elements = column.elements?.filter(shouldKeepElement) ?? [];
                });
            });
        });
    });
}
function shouldKeepElement(element) {
    if (element.type !== 'widget') {
        return true;
    }
    const widget = element.data;
    if (!widget || typeof widget !== 'object') {
        return true;
    }
    if (typeof widget.content === 'string') {
        return !isHtmlEmpty(widget.content);
    }
    return true;
}
function isHtmlEmpty(html) {
    if (!html.trim()) {
        return true;
    }
    const container = document.createElement('div');
    container.innerHTML = html;
    if (container.textContent?.trim()) {
        return false;
    }
    return !container.querySelector('img,svg,video,audio,iframe,canvas,table,hr,ul,ol,blockquote');
}

class FormJsonSerializer {
    constructor() {
        this.sectionRenderer = new SectionRenderer();
        this.meshRenderer = new MeshRenderer();
        this.rowRenderer = new RowRenderer();
        this.columnRenderer = new ColumnRenderer();
        this.hiddenBlockRenderer = new HiddenBlockRenderer();
        this.hiddenFieldRenderer = new HiddenFieldRenderer();
        this.widgetRendererFactory = new WidgetRendererFactory();
        this.fieldRendererFactory = new FieldRendererFactory();
        this.specialRendererFactory = new SpecialRendererFactory();
    }
    toJSON(container, styles) {
        const result = {
            sections: []
        };
        const sectionElements = DomQuery.getSections(container);
        result.sections = sectionElements.map(section => this.serializeSection(section));
        const hiddenFieldsData = this.serializeHiddenFields(container);
        if (hiddenFieldsData) {
            result.hiddenFields = hiddenFieldsData;
        }
        if (styles !== undefined) {
            result.styles = { ...styles };
        }
        return result;
    }
    serializeSection(sectionElement) {
        const sectionType = DomQuery.getSectionType(sectionElement) ?? 'form';
        const meshElements = DomQuery.getMeshes(sectionElement);
        return {
            id: sectionElement.id,
            type: sectionType,
            meshes: meshElements.map(mesh => this.serializeMesh(mesh))
        };
    }
    serializeMesh(meshElement) {
        const meshContainer = DomQuery.getMeshContainer(meshElement);
        const rowElements = meshContainer
            ? Array.from(meshContainer.querySelectorAll(':scope > [data-mesh-row]'))
            : [];
        return {
            id: meshElement.id,
            rows: rowElements.map(row => this.serializeRow(row))
        };
    }
    serializeRow(rowElement) {
        const colElements = Array.from(rowElement.querySelectorAll(':scope > [data-mesh-col]'));
        return {
            id: rowElement.id,
            columns: colElements.map(col => this.serializeColumn(col))
        };
    }
    serializeColumn(columnElement) {
        const elements = [];
        const childElements = DomQuery.getElements(columnElement);
        childElements.forEach(element => {
            const elementData = this.serializeColumnElement(element);
            if (elementData) {
                elements.push(elementData);
            }
        });
        return {
            id: columnElement.id,
            size: DomQuery.getColumnSize(columnElement).toString(),
            elements
        };
    }
    serializeColumnElement(element) {
        const jsonData = element.dataset['elementJson'];
        if (!jsonData)
            return null;
        try {
            const parsed = JSON.parse(jsonData);
            const elementType = element.getAttribute('data-element');
            if (elementType === 'field') {
                return { type: 'field', data: parsed };
            }
            else {
                return { type: 'widget', data: parsed };
            }
        }
        catch (e) {
            console.error('[toJSON] Failed to parse element JSON:', e);
            return null;
        }
    }
    serializeHiddenFields(container) {
        const hiddenContainer = container.querySelector(SelectorUtil.hiddenVarsBlock.base);
        if (!hiddenContainer || hiddenContainer.childElementCount === 0) {
            return null;
        }
        const hiddenElements = Array.from(hiddenContainer.querySelectorAll(SelectorUtil.builderElement.hidden));
        const fields = [];
        hiddenElements.forEach(element => {
            const jsonData = element.dataset['elementJson'];
            if (jsonData) {
                try {
                    fields.push(JSON.parse(jsonData));
                }
                catch (e) {
                    console.warn('Failed to parse hidden field JSON:', element.id);
                }
            }
        });
        return fields.length > 0 ? { fields } : null;
    }
    renderFromJSON(data, options) {
        const fragment = document.createDocumentFragment();
        if (!data?.sections)
            return fragment;
        this.currentRuntimeContext = options?.runtimeContext;
        this.currentBaseUrl = options?.baseUrl;
        // Apply variable replacement to the data if variables are provided
        const currentVariables = options?.variables;
        const processedData = currentVariables && Object.keys(currentVariables).length > 0
            ? applyVariablesToPageData(data, currentVariables)
            : data;
        processedData.sections.forEach((sectionData) => {
            const sectionElement = this.renderSection(sectionData);
            fragment.appendChild(sectionElement);
        });
        const hiddenBlock = this.renderHiddenFieldsBlock(processedData.hiddenFields, options?.pageId);
        if (hiddenBlock) {
            fragment.appendChild(hiddenBlock);
        }
        this.injectSystemFields(fragment, options?.runtimeContext);
        this.currentRuntimeContext = undefined;
        this.currentBaseUrl = undefined;
        return fragment;
    }
    injectSystemFields(fragment, runtimeContext) {
        if (!runtimeContext) {
            return;
        }
        if (runtimeContext.account?.accountId) {
            const accountIdRenderer = this.specialRendererFactory.getRenderer('accountId');
            const accountIdElement = accountIdRenderer.createNew({ runtimeContext });
            fragment.appendChild(accountIdElement);
        }
    }
    renderSection(sectionData) {
        const normalizedSectionType = (sectionData.type || 'form').toLowerCase();
        const sectionElement = this.sectionRenderer.render({
            id: sectionData.id,
            type: 'section',
            sectionType: normalizedSectionType
        });
        sectionData.meshes?.forEach(meshData => {
            const meshElement = this.renderMesh(meshData);
            sectionElement.appendChild(meshElement);
        });
        return sectionElement;
    }
    renderMesh(meshData) {
        const meshElement = this.meshRenderer.render({
            id: meshData.id,
            type: 'mesh'
        });
        const meshContainer = meshElement.querySelector('[data-mesh-container]');
        if (!meshContainer)
            return meshElement;
        meshData.rows?.forEach(rowData => {
            const rowElement = this.renderRow(rowData);
            meshContainer.appendChild(rowElement);
        });
        return meshElement;
    }
    renderRow(rowData) {
        const rowElement = this.rowRenderer.render({
            id: rowData.id,
            type: 'row'
        });
        rowData.columns?.forEach(columnData => {
            const columnElement = this.renderColumn(columnData);
            rowElement.appendChild(columnElement);
        });
        const hasElements = rowData.columns?.some(c => c.elements?.length > 0);
        if (hasElements) {
            rowElement.classList.remove('empty');
        }
        DomQuery.updateFirstLastColumnClasses(rowElement);
        return rowElement;
    }
    renderColumn(columnData) {
        const size = columnData.size ? parseInt(columnData.size, 10) : 12;
        const columnElement = this.columnRenderer.render({ id: columnData.id, type: 'column', size }, { empty: !columnData.elements || columnData.elements.length === 0 });
        columnData.elements?.forEach(elementData => {
            const element = this.renderColumnElement(elementData);
            if (element) {
                columnElement.appendChild(element);
            }
        });
        return columnElement;
    }
    renderColumnElement(elementData) {
        const renderOptions = (this.currentRuntimeContext || this.currentBaseUrl)
            ? { runtimeContext: this.currentRuntimeContext, baseUrl: this.currentBaseUrl }
            : undefined;
        const elementType = elementData.type.toLowerCase();
        if (elementType === 'field') {
            const fieldData = elementData.data;
            const fieldType = fieldData.type;
            const fieldTypeLower = fieldType.toLowerCase();
            if (fieldTypeLower === 'captcha' || fieldTypeLower === 'gdpr' || fieldTypeLower === 'accountid') {
                const specialType = fieldTypeLower === 'accountid' ? 'accountId' : fieldTypeLower;
                const renderer = this.specialRendererFactory.getRenderer(specialType);
                return renderer.render(fieldData, renderOptions);
            }
            const renderer = this.fieldRendererFactory.getRenderer(fieldType);
            return renderer.render(fieldData, renderOptions);
        }
        else if (elementType === 'widget') {
            const widgetData = elementData.data;
            const renderer = this.widgetRendererFactory.getRenderer(widgetData.type);
            return renderer.render(widgetData, renderOptions);
        }
        console.warn('[TruForms] Unknown element type:', elementData.type);
        return null;
    }
    renderHiddenFieldsBlock(hiddenFieldsData, pageId) {
        if (!hiddenFieldsData?.fields?.length) {
            return null;
        }
        const container = this.hiddenBlockRenderer.render(pageId);
        hiddenFieldsData.fields.forEach(fieldData => {
            const fieldElement = this.hiddenFieldRenderer.render(fieldData);
            container.appendChild(fieldElement);
        });
        return container;
    }
}

/**
 * Creates an IMask instance for date input with the specified format.
 * Can be used standalone or within a larger preparer architecture.
 */
function createDateMask$1(input, format, options = {}) {
    const maskPattern = getMaskPattern(format);
    const mask = IMask(input, {
        mask: maskPattern,
        lazy: options.lazy ?? true,
        overwrite: true,
        blocks: {
            d: {
                mask: IMask.MaskedRange,
                from: 1,
                to: 31,
                maxLength: 2,
                autofix: 'pad',
                placeholderChar: 'D',
            },
            m: {
                mask: IMask.MaskedRange,
                from: 1,
                to: 12,
                maxLength: 2,
                autofix: 'pad',
                placeholderChar: 'M',
            },
            Y: {
                mask: IMask.MaskedRange,
                from: 1,
                to: 9999,
                maxLength: 4,
                autofix: false,
                placeholderChar: 'Y',
            },
        },
    });
    const focusHandler = () => {
        mask.updateOptions({ lazy: false });
        options.onFocus?.();
    };
    const blurHandler = () => {
        if (!/\d/.test(input.value)) {
            mask.updateOptions({ lazy: true });
        }
        options.onBlur?.();
    };
    input.addEventListener('focus', focusHandler);
    input.addEventListener('blur', blurHandler);
    return mask;
}
function getMaskPattern(format) {
    const lowerFormat = format.toLowerCase();
    if (lowerFormat.startsWith('d')) {
        return 'd/`m/`Y';
    }
    return 'm/`d/`Y';
}
function fillCurrentDate(input, format, mask) {
    if (input.dataset['fillCurrentDate'] !== 'true' || input.value) {
        return;
    }
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = String(today.getFullYear()).padStart(4, '0');
    mask.value = format.toLowerCase().startsWith('d')
        ? `${day}/${month}/${year}`
        : `${month}/${day}/${year}`;
}
class DateFieldPreparer {
    constructor(fieldElement, options = {}) {
        this.fieldElement = fieldElement;
        this.options = options;
        this.mask = null;
    }
    prepare() {
        const inputElement = this.fieldElement.querySelector('input');
        if (!inputElement) {
            return;
        }
        const format = this.getDateFormat(inputElement);
        this.mask = createDateMask$1(inputElement, format);
        fillCurrentDate(inputElement, format, this.mask);
        this.prepareListeners(inputElement);
    }
    destroy() {
        if (this.mask) {
            this.mask.destroy();
            this.mask = null;
        }
    }
    updateValue() {
        if (this.mask) {
            this.mask.updateValue();
        }
    }
    prepareListeners(input) {
        if (this.options.onFocus) {
            input.addEventListener('focus', (event) => {
                this.options.onFocus?.(event);
            });
        }
        if (this.options.onBlur) {
            input.addEventListener('blur', () => {
                this.options.onBlur?.(this.fieldElement);
            });
        }
        if (this.options.onChange) {
            input.addEventListener('change', () => {
                this.options.onChange?.();
            });
        }
    }
    getDateFormat(input) {
        // Try to get format from data-format attribute (new approach)
        const dataFormat = input.dataset['format'];
        if (dataFormat) {
            return dataFormat;
        }
        // Fallback to data-form-field-meta-info (legacy Base64 approach)
        const metaInfo = this.fieldElement.getAttribute('data-form-field-meta-info');
        if (metaInfo) {
            try {
                const decoded = JSON.parse(atob(metaInfo));
                if (decoded?.format) {
                    return decoded.format;
                }
            }
            catch {
                // Ignore parse errors
            }
        }
        // Default format
        return 'MM/dd/yyyy';
    }
}
/**
 * Initialize date fields in a container element
 */
function initializeDateFields(container, options = {}) {
    const dateFields = container.querySelectorAll('[data-element="field"][data-field="date"], [data-element="field"][data-field="birthday"]');
    const preparers = [];
    dateFields.forEach(field => {
        const preparer = new DateFieldPreparer(field, options);
        preparer.prepare();
        preparers.push(preparer);
    });
    return preparers;
}

class SignatureFieldPreparer {
    constructor(fieldElement, options = {}) {
        this.fieldElement = fieldElement;
        this.options = options;
        this.signaturePad = null;
        this.resizeObserver = null;
        this.predefinedDataUrl = null;
    }
    get element() {
        return this.fieldElement;
    }
    prepare() {
        const canvas = this.fieldElement.querySelector('canvas');
        const overlay = this.fieldElement.querySelector('.overlay');
        const clearButton = this.fieldElement.querySelector('.signature-clear');
        const fileInput = this.fieldElement.querySelector('.input');
        if (!canvas || !fileInput) {
            return;
        }
        const setOverlayVisible = (visible) => {
            if (overlay) {
                overlay.style.display = visible ? '' : 'none';
            }
        };
        const updateOverlay = () => {
            setOverlayVisible(this.signaturePad?.isEmpty() ?? true);
        };
        const clearFileInput = () => {
            fileInput.files = new DataTransfer().files;
        };
        const resizeCanvas = () => {
            if (!this.signaturePad) {
                return;
            }
            const width = canvas.offsetWidth;
            const height = canvas.offsetHeight;
            if (!width || !height) {
                return;
            }
            const data = this.signaturePad?.toData();
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = width * ratio;
            canvas.height = height * ratio;
            canvas.getContext('2d')?.scale(ratio, ratio);
            this.signaturePad?.clear();
            if (data && data.length > 0) {
                this.signaturePad?.fromData(data);
            }
            else if (this.predefinedDataUrl) {
                this.signaturePad?.fromDataURL(this.predefinedDataUrl);
            }
        };
        const emitChangeEvent = () => {
            this.options.onChange?.(this.fieldElement);
            if (this.options.onBlur) {
                this.options.onBlur(this.fieldElement);
            }
            updateOverlay();
        };
        const updateFileInput = () => {
            if (!this.signaturePad) {
                return;
            }
            if (this.signaturePad.isEmpty()) {
                clearFileInput();
                emitChangeEvent();
                return;
            }
            const dataUrl = this.signaturePad.toDataURL('image/png');
            const file = this.dataUrlToFile(dataUrl, 'signature.png');
            const dt = new DataTransfer();
            dt.items.add(file);
            fileInput.files = dt.files;
            emitChangeEvent();
        };
        this.signaturePad = new SignaturePad(canvas);
        this.resizeObserver = new ResizeObserver(() => {
            resizeCanvas();
        });
        this.resizeObserver.observe(canvas);
        window.addEventListener('resize', resizeCanvas);
        this.signaturePad.addEventListener('beginStroke', () => {
            if (this.predefinedDataUrl) {
                this.predefinedDataUrl = null;
                canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
            }
            setOverlayVisible(false);
        });
        this.signaturePad.addEventListener('endStroke', () => updateFileInput());
        clearButton?.addEventListener('click', event => {
            event.preventDefault();
            this.predefinedDataUrl = null;
            this.signaturePad?.clear();
            clearFileInput();
            emitChangeEvent();
        });
    }
    async loadFromDataURL(dataUrl) {
        if (!this.signaturePad) {
            return;
        }
        this.predefinedDataUrl = dataUrl;
        await this.signaturePad.fromDataURL(dataUrl);
        const overlay = this.fieldElement.querySelector('.overlay');
        if (overlay) {
            overlay.style.display = 'none';
        }
        const fileInput = this.fieldElement.querySelector('.input');
        if (fileInput) {
            const file = this.dataUrlToFile(dataUrl, 'signature.png');
            const dt = new DataTransfer();
            dt.items.add(file);
            fileInput.files = dt.files;
        }
        this.options.onChange?.(this.fieldElement);
    }
    dataUrlToFile(dataUrl, fileName) {
        const [meta, base64] = dataUrl.split(',');
        const mime = meta.match(/:(.*?);/)?.[1] ?? 'image/png';
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return new File([bytes], fileName, { type: mime });
    }
    destroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.signaturePad) {
            this.signaturePad = null;
        }
    }
}
/**
 * Initialize signature fields in a container element
 */
function initializeSignatureFields(container, options = {}) {
    const signatureFields = container.querySelectorAll('[data-element="field"][data-field="signature"]');
    const preparers = [];
    signatureFields.forEach(field => {
        const preparer = new SignatureFieldPreparer(field, options);
        preparer.prepare();
        preparers.push(preparer);
    });
    return preparers;
}

class FormPageHistory {
    constructor(maxStack = 20) {
        this.maxStack = maxStack;
        if (maxStack < 1) {
            throw new Error('FormPageHistory maxStack must be greater than 0');
        }
        this.undoRecords = [];
        this.doRecords = [];
    }
    get canUndo() {
        return this.undoRecords.length > 1;
    }
    get canRedo() {
        return this.doRecords.length > 0;
    }
    get current() {
        return this.undoRecords.at(-1) ?? null;
    }
    reset() {
        this.undoRecords.length = 0;
        this.doRecords.length = 0;
    }
    save(currentState) {
        this.doRecords.length = 0;
        this.undoRecords.push(currentState);
        while (this.undoRecords.length > this.maxStack) {
            this.undoRecords.shift();
        }
    }
    undo() {
        if (this.canUndo) {
            const record = this.undoRecords.pop();
            if (record != null) {
                this.doRecords.push(record);
            }
            return this.undoRecords.at(-1) ?? null;
        }
        return null;
    }
    redo() {
        if (this.canRedo) {
            const record = this.doRecords.pop();
            if (record != null) {
                this.undoRecords.push(record);
            }
            return record ?? null;
        }
        return null;
    }
}

class FormHistoryItem {
    constructor(content) {
        this.id = crypto.randomUUID();
        this.timestamp = new Date();
        this.content = content;
    }
}

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
function isValidEmail(value) {
    if (!value || value.trim().length === 0)
        return true; // Empty is valid (use required check separately)
    return EMAIL_REGEX.test(value.trim());
}
const TEL_PATTERN_REGEX = /^[\d\s\-()+]+$/;
const MIN_TEL_DIGITS = 10;
const MAX_TEL_LENGTH = 20;
function isValidTelephone(value) {
    if (!value || value.trim().length === 0)
        return true;
    const trimmed = value.trim();
    // Check pattern (only digits, spaces, dashes, parentheses, plus)
    if (!TEL_PATTERN_REGEX.test(trimmed)) {
        return false;
    }
    // Check digit count
    const digitCount = (trimmed.match(/\d/g) || []).length;
    if (digitCount < MIN_TEL_DIGITS) {
        return false;
    }
    // Check total length
    if (trimmed.length > MAX_TEL_LENGTH) {
        return false;
    }
    return true;
}
const URL_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-./?%&=]*)?$/i;
function isValidUrl(value) {
    if (!value || value.trim().length === 0)
        return true;
    return URL_REGEX.test(value.trim());
}
function isValidNumber(value, min, max) {
    if (!value || value.trim().length === 0)
        return true;
    const num = parseFloat(value);
    if (isNaN(num))
        return false;
    if (min !== undefined && num < min)
        return false;
    if (max !== undefined && num > max)
        return false;
    return true;
}
const DATE_PLACEHOLDER_CHARS = ['D', 'M', 'Y'];
function isDateIncomplete(value) {
    if (!value)
        return false;
    return DATE_PLACEHOLDER_CHARS.some(char => value.includes(char));
}
const MAX_FILE_SIZE_MB = 28;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
function isFileSizeValid(file) {
    return file.size <= MAX_FILE_SIZE_BYTES;
}
const NAME_REGEX = /^[\p{L}'\- ]+$/u;
function isValidName(value) {
    if (!value || value.trim().length === 0)
        return true; // Empty is valid (use required check separately)
    return NAME_REGEX.test(value.trim());
}
const COMPANY_NAME_REGEX = /^[A-Za-zА-Яа-яЁё0-9\s\-.&()]+$/;
function isValidCompanyName(value) {
    if (!value || value.trim().length === 0)
        return true; // Empty is valid (use required check separately)
    return COMPANY_NAME_REGEX.test(value.trim());
}
function isValidBirthday(value, format) {
    let day;
    let month;
    let year;
    let regex;
    if (format === 'dd/MM/yyyy') {
        regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{4})$/;
        if (!regex.test(value))
            return false;
        const parts = value.split('/').map(Number);
        [day, month, year] = parts;
    }
    else {
        // default MM/dd/yyyy
        regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(\d{4})$/;
        if (!regex.test(value))
            return false;
        const parts = value.split('/').map(Number);
        [month, day, year] = parts;
    }
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date > today)
        return false;
    const minDate = new Date();
    minDate.setFullYear(today.getFullYear() - 120);
    minDate.setHours(0, 0, 0, 0);
    if (date < minDate)
        return false;
    return true;
}

const MAX_DROPDOWN_HEIGHT = 400;
const DROPDOWN_PADDING = 8;
function toggleDropdown(container, forceState) {
    const isActive = forceState ?? !container.classList.contains('active');
    container.classList.toggle('active', isActive);
    return isActive;
}
function isDropdownOpen(container) {
    return container.classList.contains('active');
}
function adjustDropdownPosition(elements, context) {
    const { container, options } = elements;
    const { viewportHeight, frameTop = 0, formContainerRect } = context;
    const dropdownRect = container.getBoundingClientRect();
    // Calculate available space
    let spaceBelow;
    let spaceAbove;
    if (formContainerRect) {
        // More precise calculation with form container bounds
        spaceBelow = Math.min(formContainerRect.bottom - dropdownRect.bottom, viewportHeight - dropdownRect.bottom - frameTop);
        spaceAbove = dropdownRect.top - Math.max(formContainerRect.top, frameTop);
    }
    else {
        // Simple calculation
        spaceBelow = viewportHeight - dropdownRect.bottom;
        spaceAbove = dropdownRect.top;
    }
    // Get options height (temporarily show if hidden)
    const wasHidden = options.style.display === 'none';
    if (wasHidden) {
        options.style.visibility = 'hidden';
        options.style.display = 'block';
    }
    const optionsHeight = options.scrollHeight;
    if (wasHidden) {
        options.style.display = '';
        options.style.visibility = '';
    }
    // Determine direction
    const renderBelow = spaceBelow >= optionsHeight || spaceBelow >= spaceAbove;
    if (renderBelow) {
        options.style.top = '100%';
        options.style.bottom = 'unset';
        options.style.maxHeight = `${Math.min(spaceBelow - DROPDOWN_PADDING, MAX_DROPDOWN_HEIGHT)}px`;
        return 'bottom';
    }
    else {
        options.style.bottom = '100%';
        options.style.top = 'unset';
        options.style.maxHeight = `${Math.min(spaceAbove - DROPDOWN_PADDING, MAX_DROPDOWN_HEIGHT)}px`;
        return 'top';
    }
}
function selectOption(elements, optionIndex, callbacks) {
    const { container, input, optionItems } = elements;
    const items = Array.from(optionItems);
    if (optionIndex < 0 || optionIndex >= items.length) {
        return;
    }
    const option = items[optionIndex];
    const value = option.textContent || '';
    input.value = value;
    toggleDropdown(container, false);
    container.focus();
    // Dispatch native change event
    input.dispatchEvent(new Event('change', { bubbles: true }));
    callbacks?.onSelect?.(value);
    callbacks?.onChange?.();
}
function highlightOption(optionItems, index, highlightClass = 'highlighted') {
    const items = Array.from(optionItems);
    items.forEach((option, i) => {
        if (i === index) {
            option.classList.add(highlightClass);
            option.scrollIntoView({ block: 'nearest' });
        }
        else {
            option.classList.remove(highlightClass);
        }
    });
}
function handleDropdownKeyboard(event, elements, currentIndex, callbacks) {
    const { container, optionItems } = elements;
    const items = Array.from(optionItems);
    const isActive = isDropdownOpen(container);
    let newIndex = currentIndex;
    switch (event.key) {
        case ' ':
        case 'Enter':
            event.preventDefault();
            if (!isActive) {
                toggleDropdown(container, true);
                newIndex = 0;
            }
            else if (currentIndex >= 0 && currentIndex < items.length) {
                selectOption(elements, currentIndex, callbacks);
                newIndex = -1;
            }
            break;
        case 'ArrowDown':
            event.preventDefault();
            if (!isActive) {
                toggleDropdown(container, true);
                newIndex = 0;
            }
            else {
                newIndex = Math.min(currentIndex + 1, items.length - 1);
            }
            break;
        case 'ArrowUp':
            event.preventDefault();
            if (isActive) {
                newIndex = Math.max(currentIndex - 1, 0);
            }
            break;
        case 'Escape':
            if (isActive) {
                event.preventDefault();
                toggleDropdown(container, false);
                container.focus();
                newIndex = -1;
            }
            break;
        case 'Tab':
            if (isActive) {
                toggleDropdown(container, false);
                newIndex = -1;
            }
            break;
    }
    return newIndex;
}
function setupOptionsClick(elements, callbacks) {
    const { container, input, options } = elements;
    options.addEventListener('click', (event) => {
        const target = event.target;
        if (!target.classList.contains('option'))
            return;
        const value = target.textContent || '';
        input.value = value;
        toggleDropdown(container, false);
        container.focus();
        input.dispatchEvent(new Event('change', { bubbles: true }));
        callbacks?.onSelect?.(value);
        callbacks?.onChange?.();
        event.stopPropagation();
    });
}
function setupDropdownTabulation(dropdownElement) {
    const dropdown = dropdownElement.querySelector('.dropdown');
    if (dropdown) {
        dropdown.tabIndex = 0;
    }
    const button = dropdownElement.querySelector('button');
    if (button) {
        button.tabIndex = -1;
    }
    const input = dropdownElement.querySelector('input');
    if (input) {
        input.tabIndex = -1;
    }
}
function getDropdownElements(fieldElement) {
    const container = fieldElement.querySelector('.dropdown');
    const input = fieldElement.querySelector('.dropdown-input, .input');
    const options = fieldElement.querySelector('.options');
    if (!container || !input || !options)
        return null;
    const optionItems = options.querySelectorAll('.option');
    return { container, input, options, optionItems };
}

// Default date format patterns
const DATE_FORMATS = {
    'DD/MM/YYYY': 'DD/MM/YYYY',
    'MM/DD/YYYY': 'MM/DD/YYYY',
    'YYYY-MM-DD': 'YYYY-MM-DD',
    'DD.MM.YYYY': 'DD.MM.YYYY',
    'DD-MM-YYYY': 'DD-MM-YYYY'
};
const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';
const DATE_MASK_BLOCKS = {
    d: {
        mask: IMask.MaskedRange,
        from: 1,
        to: 31,
        maxLength: 2,
        placeholderChar: 'D'
    },
    m: {
        mask: IMask.MaskedRange,
        from: 1,
        to: 12,
        maxLength: 2,
        placeholderChar: 'M'
    },
    Y: {
        mask: IMask.MaskedRange,
        from: 1900,
        to: 9999,
        placeholderChar: 'Y'
    }
};
function formatToMaskPattern(format) {
    return format
        .replace(/DD/g, 'd')
        .replace(/MM/g, 'm')
        .replace(/YYYY/g, 'Y')
        .replace(/\//g, '{/}')
        .replace(/-/g, '{-}')
        .replace(/\./g, '{.}');
}
function getDateFormatFromAttribute(fieldElement) {
    const format = fieldElement.dataset['dateFormat'];
    if (format && format in DATE_FORMATS) {
        return format;
    }
    return null;
}
function getDateFormatFromJson(fieldElement) {
    const jsonAttr = fieldElement.getAttribute('data-element-json');
    if (!jsonAttr)
        return null;
    try {
        // Try Base64 decode first
        let jsonStr = jsonAttr;
        try {
            jsonStr = atob(jsonAttr);
        }
        catch {
            // Not Base64, use as-is
        }
        const data = JSON.parse(jsonStr);
        if (data.dateFormat && data.dateFormat in DATE_FORMATS) {
            return data.dateFormat;
        }
    }
    catch {
        // Invalid JSON
    }
    return null;
}
function getDateFormat(fieldElement) {
    return getDateFormatFromAttribute(fieldElement)
        || getDateFormatFromJson(fieldElement)
        || DEFAULT_DATE_FORMAT;
}
function createDateMask(input, format, lazy = true) {
    const pattern = formatToMaskPattern(format);
    return IMask(input, {
        mask: pattern,
        blocks: DATE_MASK_BLOCKS,
        lazy,
        overwrite: true
    });
}
class DateMaskPreparer {
    constructor(input, format) {
        this.mask = null;
        this.focusHandler = null;
        this.blurHandler = null;
        this.input = input;
        this.format = format;
    }
    prepare() {
        // Create mask in lazy mode (placeholder hidden until focus)
        this.mask = createDateMask(this.input, this.format, true);
        // Show placeholder on focus
        this.focusHandler = () => {
            if (this.mask) {
                this.mask.updateOptions({ lazy: false });
            }
        };
        // Hide placeholder on blur if empty
        this.blurHandler = () => {
            if (this.mask && !this.mask.unmaskedValue) {
                this.mask.updateOptions({ lazy: true });
            }
        };
        this.input.addEventListener('focus', this.focusHandler);
        this.input.addEventListener('blur', this.blurHandler);
    }
    destroy() {
        if (this.focusHandler) {
            this.input.removeEventListener('focus', this.focusHandler);
        }
        if (this.blurHandler) {
            this.input.removeEventListener('blur', this.blurHandler);
        }
        if (this.mask) {
            this.mask.destroy();
            this.mask = null;
        }
    }
    getMask() {
        return this.mask;
    }
}
function initializeDateMasks(container) {
    const preparers = [];
    const dateFields = container.querySelectorAll('[data-field="date"]');
    dateFields.forEach(fieldElement => {
        const input = fieldElement.querySelector('input[type="text"]');
        if (!input)
            return;
        const format = getDateFormat(fieldElement);
        const preparer = new DateMaskPreparer(input, format);
        preparer.prepare();
        preparers.push(preparer);
    });
    return preparers;
}

const ID_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const ID_LENGTH = 8;
function generateElementId(prefix = 'el') {
    let result = '';
    for (let i = 0; i < ID_LENGTH; i++) {
        result += ID_ALPHABET.charAt(Math.floor(Math.random() * ID_ALPHABET.length));
    }
    return `${prefix}-${result}`;
}
function generateUniqueElementId(existingIds = [], prefix = 'el', document = globalThis.document) {
    let id = generateElementId(prefix);
    let attempts = 0;
    const maxAttempts = 100;
    while ((existingIds.includes(id) || document.getElementById(id)) &&
        attempts < maxAttempts) {
        id = generateElementId(prefix);
        attempts++;
    }
    return id;
}
function clearElement(element) {
    while (element.firstChild) {
        element.removeChild(element.firstChild);
    }
}
function getBoundingClientRectFromHiddenElement(element) {
    const originalDisplay = element.style.display;
    const originalVisibility = element.style.visibility;
    const originalPosition = element.style.position;
    // Make visible but hidden
    element.style.display = 'block';
    element.style.visibility = 'hidden';
    element.style.position = 'absolute';
    const rect = element.getBoundingClientRect();
    // Restore original styles
    element.style.display = originalDisplay;
    element.style.visibility = originalVisibility;
    element.style.position = originalPosition;
    return rect;
}
function isElementInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth));
}
function scrollToElement(element, options = { behavior: 'smooth', block: 'center' }) {
    element.scrollIntoView(options);
}
function findAncestor(element, selector) {
    return element.closest(selector);
}
function elementMatches(element, selector) {
    return element.matches(selector);
}
function getFieldElements(container) {
    return Array.from(container.querySelectorAll('[data-element="field"]'));
}
function getFieldType(fieldElement) {
    return fieldElement.dataset['field'] || null;
}
function getFieldLabel(fieldElement) {
    // Try data-label attribute
    const dataLabel = fieldElement.dataset['label'];
    if (dataLabel) {
        return dataLabel;
    }
    // Try label element
    const labelElement = fieldElement.querySelector('.label');
    if (labelElement?.textContent) {
        return labelElement.textContent.trim();
    }
    // Try input's data-label
    const input = fieldElement.querySelector('input, textarea, select');
    if (input?.dataset['label']) {
        return input.dataset['label'];
    }
    // Fallback to field ID
    return fieldElement.id || 'Field';
}
function isFieldRequired(fieldElement) {
    // Check class
    if (fieldElement.classList.contains('required')) {
        return true;
    }
    // Check input required attribute
    const input = fieldElement.querySelector('input, textarea, select');
    if (input?.required) {
        return true;
    }
    // Check data attribute
    if (fieldElement.dataset['required'] === 'true') {
        return true;
    }
    return false;
}

const DEFAULT_OPTIONS = {
    errorClass: 'error',
    errorMessageClass: 'error-message',
    errorContainerClass: 'error-container'
};
function createErrorElement(message, options = DEFAULT_OPTIONS) {
    const errorElement = document.createElement('div');
    errorElement.classList.add(options.errorMessageClass || 'error-message');
    errorElement.textContent = message;
    return errorElement;
}
function showFieldError(fieldElement, message, options = DEFAULT_OPTIONS) {
    // Add error class to field
    fieldElement.classList.add(options.errorClass || 'error');
    // Remove existing error message if any
    clearFieldError(fieldElement, options);
    // Find the element to insert error after (usually input or .field container)
    const inputContainer = fieldElement.querySelector('.field') || fieldElement;
    const input = inputContainer.querySelector('input, textarea, select, .dropdown');
    const errorElement = createErrorElement(message, options);
    if (input) {
        input.parentElement?.insertBefore(errorElement, input.nextSibling);
    }
    else {
        inputContainer.appendChild(errorElement);
    }
}
function clearFieldError(fieldElement, options = DEFAULT_OPTIONS) {
    // Remove error class
    fieldElement.classList.remove(options.errorClass || 'error');
    // Remove error message element
    const errorMessage = fieldElement.querySelector(`.${options.errorMessageClass || 'error-message'}`);
    errorMessage?.remove();
    // Also remove error class from nested elements
    const nestedErrorElements = fieldElement.querySelectorAll(`.${options.errorClass || 'error'}`);
    nestedErrorElements.forEach(el => el.classList.remove(options.errorClass || 'error'));
}
function clearAllErrors(container, options = DEFAULT_OPTIONS) {
    // Remove all error messages
    const errorMessages = container.querySelectorAll(`.${options.errorMessageClass || 'error-message'}`);
    errorMessages.forEach(el => el.remove());
    // Remove all error classes
    const errorElements = container.querySelectorAll(`.${options.errorClass || 'error'}`);
    errorElements.forEach(el => el.classList.remove(options.errorClass || 'error'));
}
function showInputError(input, message, options = DEFAULT_OPTIONS) {
    // Add error class to input
    input.classList.add(options.errorClass || 'error');
    // Remove existing error message if any
    const existingError = input.parentElement?.querySelector(`.${options.errorMessageClass || 'error-message'}`);
    existingError?.remove();
    // Create and insert error message
    const errorElement = createErrorElement(message, options);
    input.parentElement?.insertBefore(errorElement, input.nextSibling);
}
function clearInputError(input, options = DEFAULT_OPTIONS) {
    input.classList.remove(options.errorClass || 'error');
    const errorMessage = input.parentElement?.querySelector(`.${options.errorMessageClass || 'error-message'}`);
    errorMessage?.remove();
}
function scrollToFirstError(container, options = DEFAULT_OPTIONS) {
    const firstError = container.querySelector(`.${options.errorClass || 'error'}`);
    if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
}

function utf8Encode(str) {
    return encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16)));
}
function utf8Decode(bytes) {
    return decodeURIComponent(bytes
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''));
}
function serialize(obj) {
    try {
        const jsonStr = JSON.stringify(obj);
        return btoa(utf8Encode(jsonStr));
    }
    catch (e) {
        console.error('Base64 serialize error:', e);
        return '';
    }
}
function deserialize(base64) {
    try {
        const jsonStr = utf8Decode(atob(base64));
        return JSON.parse(jsonStr);
    }
    catch (e) {
        console.error('Base64 deserialize error:', e);
        return null;
    }
}
function isBase64(str) {
    if (!str || str.length === 0)
        return false;
    try {
        return btoa(atob(str)) === str;
    }
    catch {
        return false;
    }
}
function safeDecode(base64) {
    try {
        return atob(base64);
    }
    catch {
        return null;
    }
}
function safeEncode(str) {
    try {
        return btoa(str);
    }
    catch {
        return '';
    }
}

function isSafari() {
    return /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
}
function isFirefox() {
    return /^((?!chrome|android|crios|fxios).)*firefox/i.test(navigator.userAgent);
}
function isChrome() {
    return /chrome|chromium|crios/i.test(navigator.userAgent) && !/edge|edg/i.test(navigator.userAgent);
}
function isEdge() {
    return /edge|edg/i.test(navigator.userAgent);
}
function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
}
function isAndroid() {
    return /android/i.test(navigator.userAgent);
}
function isMobile() {
    return isIOS() || isAndroid() || /webOS|BlackBerry|Opera Mini|IEMobile/i.test(navigator.userAgent);
}

function isRelativeUrl(url) {
    if (!url) {
        return true;
    }
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
        return false;
    }
    return true;
}
function uncapitalize(str) {
    if (!str)
        return str;
    if (str.charAt(0) === str.charAt(0).toUpperCase()) {
        return str.charAt(0).toLocaleLowerCase() + str.slice(1);
    }
    return str;
}
function capitalize(str) {
    if (!str)
        return str;
    return str[0].toUpperCase() + str.substring(1);
}
function isNullOrWhiteSpace(str) {
    if (typeof str === 'undefined' || str === null) {
        return true;
    }
    return str.replace(/\s/g, '').length < 1;
}
function isAlpha(ch) {
    if (ch >= 'A' && ch <= 'Z')
        return true;
    if (ch >= 'a' && ch <= 'z')
        return true;
    if (ch >= 'А' && ch <= 'Я')
        return true;
    if (ch >= 'а' && ch <= 'я')
        return true;
    return false;
}
function isEnglishAlpha(ch) {
    return (ch >= 'A' && ch <= 'Z') || (ch >= 'a' && ch <= 'z');
}
function isDigit(ch) {
    return ch >= '0' && ch <= '9';
}
function getNumberFromStr(str, defaultValue = 0) {
    const number = Number.parseFloat(str.replace(',', '.'));
    return isNaN(number) ? defaultValue : number;
}
function removeLineBreak(str) {
    return str.replace(/(\r\n|\n|\r)/gm, '');
}
function removeExtraWhiteSpaces(str) {
    return str.replace(/\s+/g, ' ');
}

function getObjValueByStr(obj, path) {
    try {
        // Convert bracket notation to dot notation
        let normalizedPath = path.replace(/\[(\w+)\]/g, '.$1');
        normalizedPath = normalizedPath.replace(/^\./, '');
        const parts = normalizedPath.split('.');
        let current = obj;
        for (const part of parts) {
            if (current === null || current === undefined) {
                return undefined;
            }
            if (typeof current === 'object' && part in current) {
                current = current[part];
            }
            else {
                return undefined;
            }
        }
        return current;
    }
    catch {
        return undefined;
    }
}
function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function deepMerge(target, source) {
    const result = { ...target };
    for (const key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            const targetValue = result[key];
            const sourceValue = source[key];
            if (isPlainObject(targetValue) && isPlainObject(sourceValue)) {
                result[key] = deepMerge(targetValue, sourceValue);
            }
            else if (sourceValue !== undefined) {
                result[key] = sourceValue;
            }
        }
    }
    return result;
}

/**
 * Shared utilities for TruForms.
 * Can be used in both iframe renderer and web component.
 */
// Validators

/*
 * Public API Surface of tru-forms-builder
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AccountIdRenderer, BaseCrmFieldData, BaseSpecialRenderer, BirthdayFieldRenderer, CaptchaRenderer, CheckboxFieldRenderer, CompanyNameFieldRenderer, ContactFieldData, CustomFieldData, DATE_FORMATS, DATE_MASK_BLOCKS, DateFieldPreparer, DateFieldRenderer, DateMaskPreparer, DomQuery, DomUtils, DropdownFieldRenderer, ElementUtils, EmailFieldRenderer, FieldRendererFactory, FileFieldRenderer, FirstNameFieldRenderer, FormHistoryItem, FormJsonSerializer, FormPageHistory, FormStylesDataService, GdprRenderer, HiddenFieldRenderer, HiddenFieldTags, LastNameFieldRenderer, LinkData, NumberFieldRenderer, PasswordFieldRenderer, RadioFieldRenderer, RendererBase, SignatureFieldPreparer, SignatureFieldRenderer, SpecialRendererFactory, SubscribeFieldRenderer, TelFieldRenderer, TextFieldRenderer, TextPlaceholderType, TextareaFieldRenderer, TranslateUtils, UrlFieldRenderer, WidgetRendererFactory, adjustDropdownPosition, capitalize, clearAllErrors, clearElement, clearFieldError, clearInputError, createDateMask, createErrorElement, deepClone, deepMerge, deserialize, elementMatches, fillCurrentDate, findAncestor, formStylesDataToCssVariables, formatToMaskPattern, generateElementId, generateUniqueElementId, getAddressFieldsConfig, getBoundingClientRectFromHiddenElement, getDateFormat, getDateFormatFromAttribute, getDateFormatFromJson, getDropdownElements, getFieldElements, getFieldLabel, getFieldType, getNumberFromStr, getObjValueByStr, handleDropdownKeyboard, highlightOption, initializeDateFields, initializeDateMasks, initializeSignatureFields, isAlpha, isAndroid, isBase64, isChrome, isDateIncomplete, isDigit, isDropdownOpen, isEdge, isElementInViewport, isEnglishAlpha, isFieldRequired, isFileSizeValid, isFirefox, isIOS, isMobile, isNullOrWhiteSpace, isPlainObject, isRelativeUrl, isSafari, isValidBirthday, isValidCompanyName, isValidEmail, isValidName, isValidNumber, isValidTelephone, isValidUrl, normalizeNavDirection, normalizeSubmitVariant, removeExtraWhiteSpaces, removeLineBreak, safeDecode, safeEncode, scrollToElement, scrollToFirstError, selectOption, serialize, setupDropdownTabulation, setupOptionsClick, showFieldError, showInputError, toggleDropdown, uncapitalize };
//# sourceMappingURL=tru-visibility.tru-forms.builder.mjs.map
